import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createFileRoute,
  redirect,
  useLocation,
  useNavigate,
  useRouteContext,
} from '@tanstack/react-router';
import { generateId } from 'ai';
import {
  startTransition,
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from 'react';
import { Group, Panel } from 'react-resizable-panels';

import type {
  Project,
  UpdateDraftExternalInvitesDto,
  UpdateResearcherDraftDto,
} from '@/api/core-api/types.gen';
import type { DraftSaveFields } from '@/hooks/api/use-draft-save';

import { ProjectStatus } from '@/api/core-api';
import { KindOfResearch, ResearchConductType } from '@/api/enums';
import { notify } from '@/components/base/notifications/notifications';
import { useDraftAutoSave } from '@/hooks/api/use-draft-auto-save';
import {
  organizationCanUseAiQueryOptions,
  organizationQueryOptions,
} from '@/hooks/api/use-organization';
import { useAddProgramProject } from '@/hooks/api/use-program-mutations';
import { useScreenerRealtime } from '@/hooks/api/use-screener-realtime';
import { useBreakpoint } from '@/hooks/use-breakpoint';
import { useAnalytics } from '@/lib/analytics/use-analytics';
import {
  AI_MODERATED_INTERVIEWS_FLAG,
  orgFeatureFlagQueryOptions,
} from '@/lib/feature-flags/client/org-feature-flag-query-options';
import { useFeatureFlag } from '@/lib/feature-flags/client/use-feature-flag';
import { formatCurrency } from '@/utils/currency';
import { getCurrentSessionOptions } from '@/utils/session/session-query-options';
import { getParticipantTargetLimit, isStudyLanguage } from '@common/utils';

import type { FormQuestion } from '../-screener/types';
import type { StudyCheck } from '../-study-checks';
import type {
  AiProjectSpotlightRequest,
  AiProjectSpotlightTarget,
  AiProjectState,
  AiProjectStep,
  ClosingMessageData,
  InterviewSectionData,
  OutlineData,
  SaveStatus,
  WelcomeScreenData,
} from './-ai/types';

import {
  researcherProjectQueryOptions,
  type GetResearcherProjectResponse,
} from '../$projectId/-shared';
import {
  AdvancedScreenerAnnouncement,
  useAdvancedScreenerAnnouncement,
} from '../-components/advanced-screener-announcement';
import { DEFAULT_STUDY_LANGUAGE } from '../-components/ai-interview-settings';
import { previewRecordingPanelEnabled } from '../-preview/preview-recording-panel';
import { ProjectCanvasContent } from '../-project-canvas-content';
import {
  buildNewProjectSeed,
  resolveInitialFlow,
  type ProjectCreateNavState,
} from '../-project-create-nav-state';
import { ProjectFlowLayout } from '../-project-flow-layout';
import { ProjectPreview, type PreviewJump } from '../-project-preview';
import { QuestionChecksContext } from '../-question-checks-context';
import { ScreenerConflictAlert } from '../-screener/screener-conflict-alert';
import {
  ADVANCED_SCREENER_ANNOUNCEMENT_FLAG,
  ALLOW_ZERO_SCREENER_QUESTIONS_FLAG,
  ENABLE_SCREENER_FILE_UPLOAD_QUESTION,
  MATRIX_QUESTION_FEATURE_FLAG,
  RANK_QUESTION_FEATURE_FLAG,
  SCREENER_LIMIT_FEATURE_FLAG,
  SCREENER_LIMITS,
  STUDY_CHECKS_FEATURE_FLAG,
} from '../-screener/screener-constants';
import {
  getScreenerValidationIssues,
  resolveScreenerMinQuestions,
} from '../-screener/screener-schema';
import { apiQuestionsToForm, isScreenerDraftSavable } from '../-screener/types';
import { SmallScreenNotice } from '../-small-screen-notice';
import { resolveStudyCheckSpotlight } from '../-study-check-navigation';
import { buildStudyChecks } from '../-study-check-rules';
import { reportStudyCheckFeedback } from '../-study-checks-client';
import { buildStudyLanguageMarker } from '../-study-language-marker';
import { isAiModStudy } from '../-study-type-helpers';
import { useDismissedStudyChecks } from '../-use-dismissed-study-checks';
import { isShowingSavedScreener, useStudyChecks } from '../-use-study-checks';
import { describeTargetingAge } from './-ai/-age-targeting';
import {
  buildAiEditPrompt,
  buildFullStudyReviewPrompt,
} from './-ai/-ai-edit-prompt';
import {
  applyInterviewPatch,
  applyScreenerPatch,
  outlineChanges,
  targetingChanges,
} from './-ai/-apply-ai-patch';
import { parseChatActivity } from './-ai/-chat-activity';
import { ChatPane } from './-ai/-chat-pane';
import {
  filterOverLimitAiModFields,
  getFieldLengthLaunchErrors,
  isClosingMessageSavable,
  isInterviewSectionsSavable,
  isWelcomeScreenSavable,
} from './-ai/-field-length';
import { getHistoryShortcutAction } from './-ai/-history/history-shortcuts';
import { createHistoryMeta } from './-ai/-history/history-types';
import {
  withHistory,
  type StateWithHistory,
  type WrappedAction,
} from './-ai/-history/with-history';
import { estimateInterviewDurationMinutes } from './-ai/-interview-duration-estimate';
import { getInterviewLaunchErrors } from './-ai/-interview-issues';
import {
  buildFullPayload,
  buildInterviewSectionsPayload,
  buildClosingMessagePayload,
  buildOutlinePayload,
  buildScreenerPayload,
  buildTargetingFromProject,
  buildTargetingPayload,
  buildTrackedStatePatch,
  buildWelcomeScreenPayload,
  interviewSectionsFromProject,
} from './-ai/-payload-builders';
import { reconcileInterviewSections } from './-ai/-reconcile-interview-sections';
import { usabilityTestingOffer } from './-ai/-record-screen';
import {
  aiProjectReducer,
  clearRecordScreen,
  initialAiProjectState,
  type AiProjectAction,
} from './-ai/-reducer';
import { ResizeHandle } from './-ai/-resize-handle';
import { CHECKLIST_LABELS } from './-ai/-step-checklist';
import { getStepCompletion } from './-ai/-step-completion';
import { useAiCreateEditTelemetry } from './-ai/-use-ai-create-edit-telemetry';
import { useAnimatedPanel } from './-ai/-use-animated-panel';
import { useStepCompletionAnalytics } from './-ai/-use-project-step-analytics';
import { useQuestionFlagFix } from './-ai/-use-question-flag-fix';
import {
  type AiInterviewPatch,
  type AiOutlinePatch,
  type AiScreenerPatch,
  type AiTargetingPatch,
  useAiToolCallHandler,
} from './-ai/-use-tool-call-handler';
import { toStudyTypeId } from './-ai/build-context';
import { useAiProjectChat } from './-ai/use-ai-project-chat';
import { hasPickedAudience } from './-build-targeting-tags';
import { type DraftProject } from './-launch/drawers/draft-project';
import {
  StudyTypeDrawer,
  type StudyTypeSavePayload,
} from './-launch/drawers/study-type-drawer';
import { TargetAudienceDrawer } from './-launch/drawers/target-audience-drawer';
import { shouldResetParticipantEmailsForProjectTypeChange } from './-launch/quantitative-settings-utils';
import { ALLOW_OPTIONAL_TOPICS_FLAG } from './-launch/targeting-constants';
import { panelCountAfterKindChange } from './-participant-sources/external-participants-rules';
import {
  applyExternalInvitesChange,
  externalParticipantsFromProject,
} from './-participant-sources/external-participants-state';
import {
  type AiParticipantSourcesPatch,
  type ParticipantSourcesChange,
  resolveParticipantSourcesPatch,
} from './-participant-sources/participant-sources-patch';
import {
  DEFAULT_EXTERNAL_PARTICIPANTS_TARGET,
  EXTERNAL_PARTICIPANT_INVITES_FLAG,
  ParticipantSourcesSection,
  supportsExternalParticipants,
} from './-participant-sources/participant-sources-section';
import { QuotaCanvasSection } from './-quotas/quota-canvas-section';
import { ALL_STUDY_TYPES } from './-study-type-config';

export type { ProjectCreateNavState };

// ─── Route ───────────────────────────────────────────────────────────────────

/** Which targeting surface a patched field lights up. */
const TARGETING_SPOTLIGHT: Record<string, string> = {
  ageGroups: 'demographics',
  minAge: 'demographics',
  maxAge: 'demographics',
  genders: 'demographics',
  educationLevel: 'demographics',
  ethnicities: 'demographics',
  householdIncome: 'demographics',
  jobFunctions: 'jobProfile',
  jobTitles: 'jobProfile',
  seniority: 'jobProfile',
  companySize: 'jobProfile',
};

/** The tool the AI-edit toolbar should reach for, by the surface being edited. */
const TOOL_HINT_BY_SURFACE: Record<string, string> = {
  internalTitle: ' Use updateOutline with only internalName.',
  studyTitle:
    ' Use updateOutline with only name; touch internalName only if the researcher asked for it.',
  studyDescription: ' Use updateOutline with only description.',
  projectGoals: ' Use updateOutline with only studyGoals.',
  externalDetails: ' Use updateOutline with name and description only.',
  targeting:
    ' Use updateTargeting with only the fields that change (call lookupJobTitles first for B2B job titles).',
  screenerQuestions:
    ' Use updateScreener: append, update, or remove for changes to specific questions; replace only to regenerate the whole set.',
  interviewQuestions:
    ' Use updateInterview: updateQuestion for changes to specific questions; replace only to regenerate the whole script.',
};

export const Route = createFileRoute(
  '/researchers/_authenticated/projects/$projectId/edit',
)({
  // Render this page on the client only. The editor is fully client-interactive
  // (reducer state hydrated from the project, AI chat, drag/drop, live step
  // completion). Server rendering paints the empty initial reducer state, which
  // never matches the client's hydrated state — e.g. the progress menu renders
  // "0/4" on the server and "1/4" after hydration, tripping a hydration
  // mismatch. Ancestor beforeLoads (auth, team) and the prefetch below still run
  // on the server, so auth stays enforced and the project query stays warm.
  ssr: false,
  // Editing is only allowed for draft projects. Anything past DRAFT must view
  // the read-only details page; runtime PATCH endpoints (e.g.
  // researcher-projects.service.ts updateDetails) reject non-draft edits too.
  beforeLoad: async ({ params, context }) => {
    if (params.projectId === 'new') {
      return { projectName: null, projectOrganizationId: null };
    }

    const response = await context.queryClient.ensureQueryData(
      researcherProjectQueryOptions(params.projectId, context.user.id),
    );
    if (!response.success) {
      return { projectName: null, projectOrganizationId: null };
    }
    if (response.data.status !== ProjectStatus.DRAFT) {
      throw redirect({
        to: '/researchers/projects/$projectId/details',
        params: { projectId: params.projectId },
      });
    }
    // The editor gates every AI affordance on the PROJECT's organization
    // (`ai-opted-out`), which can differ from the active dashboard's that the
    // projects layout warmed. Settle it here so the first render already
    // knows, instead of mounting with the chat open and then collapsing it.
    if (response.data.organizationId) {
      await context.queryClient
        .ensureQueryData(
          organizationQueryOptions(
            response.data.organizationId,
            context.user.id,
          ),
        )
        .catch(() => null);
    }
    return {
      projectName: response.data.name,
      projectOrganizationId: response.data.organizationId ?? null,
    };
  },
  head: ({ match }) => ({
    meta: [{ title: t`Edit · ${match.context.projectName ?? 'project'}` }],
  }),
  component: EditProjectPage,
});

// ─── Helpers ─────────────────────────────────────────────────────────────────

/**
 * The reducer actions that bring the panel back as a source with
 * `count` participants, when a project type change drops the researcher's
 * own participants (see `computeParticipantCapping`); none otherwise.
 */
function restorePanelActions(count: number | undefined): AiProjectAction[] {
  if (count === undefined) return [];
  return [
    { type: 'PATCH_TARGETING', field: 'recruitFromPanel', value: true },
    { type: 'PATCH_TARGETING', field: 'numberOfParticipants', value: count },
  ];
}

function getStepOrder(
  isAiModEnabled: boolean,
  kindOfResearch: KindOfResearch | null,
  { recruitFromPanel = true, showScreener = true } = {},
): AiProjectStep[] {
  // A study recruiting nobody from the panel has no target audience to set,
  // and none to screen once its own participants skip the screener.
  const base: AiProjectStep[] = recruitFromPanel
    ? ['projectType', 'outline', 'targeting']
    : ['projectType', 'outline'];
  const screener: AiProjectStep[] = showScreener ? ['screener'] : [];
  if (!isAiModEnabled) return base.concat(screener);

  if (kindOfResearch === KindOfResearch.AI_MODERATED_INTERVIEW) {
    return [
      ...base,
      ...screener,
      'welcomeScreen',
      'interviewQuestions',
      'closingMessage',
    ];
  }

  return [...base, ...screener];
}

/** Resolve a study type card ID to its API fields. Defaults to ONE_ON_ONE / REMOTE for video-call, IN_PERSON for in-person. */
function resolveStudyType(studyTypeId: string): {
  kindOfResearch: KindOfResearch;
  researchConductType: ResearchConductType;
} {
  if (studyTypeId === 'video-call') {
    return {
      kindOfResearch: KindOfResearch.ONE_ON_ONE,
      researchConductType: ResearchConductType.REMOTE,
    };
  }
  if (studyTypeId === 'in-person') {
    return {
      kindOfResearch: KindOfResearch.ONE_ON_ONE,
      researchConductType: ResearchConductType.IN_PERSON,
    };
  }
  const st = ALL_STUDY_TYPES.find((s) => s.id === studyTypeId);
  if (st?.kindOfResearch != null && st.researchConductType != null) {
    return {
      kindOfResearch: st.kindOfResearch,
      researchConductType: st.researchConductType,
    };
  }
  return {
    kindOfResearch: KindOfResearch.ONE_ON_ONE,
    researchConductType: ResearchConductType.REMOTE,
  };
}

function toLayoutSaveStatus(
  s: SaveStatus,
): 'saved' | 'unsaved' | 'saving' | undefined {
  if (s === 'idle') return undefined;
  if (s === 'saving') return 'saving';
  if (s === 'saved') return 'saved';
  return 'unsaved';
}

/**
 * Build a mock project object for draft mode from the reducer's targeting
 * state. The drawer accepts `DraftProject` instead of the full `Project`,
 * so all fields below are mapped through shapes that the drawer reads.
 */
function buildMockProjectForDraft(
  state: AiProjectState,
  organizationId?: string | null,
  teamId?: string | null,
): DraftProject {
  const t = state.targeting;
  return {
    organizationId: organizationId ?? undefined,
    teamId: teamId ?? undefined,

    // Study type
    kindOfResearch: state.kindOfResearch ?? undefined,
    researchConductType: state.researchConductType ?? undefined,
    duration: state.duration ?? 45,
    interviewDeliveryMode: state.interviewDeliveryMode ?? undefined,
    studyLanguage: state.studyLanguage,
    responseLanguages: state.responseLanguages,
    numberOfParticipants: t.numberOfParticipants || undefined,

    // Audience type + demographics
    talkToAudience: t.talkToAudience ?? undefined,
    countries: t.countries as DraftProject['countries'],
    ageGroups: t.ageGroups as DraftProject['ageGroups'],
    minAge: t.minAge ?? undefined,
    maxAge: t.maxAge ?? undefined,
    genders: (t.genders || undefined) as DraftProject['genders'],
    education: t.educationLevel as DraftProject['education'],
    ethnicity: t.ethnicities as DraftProject['ethnicity'],
    householdIncome: t.householdIncome as DraftProject['householdIncome'],

    // B2B
    jobFunctions: t.jobFunctions as DraftProject['jobFunctions'],
    seniority: t.seniority as DraftProject['seniority'],
    companySize: t.companySize as DraftProject['companySize'],
    professionalIndustries: t.industries,
    professionalJobTitles: t.jobTitles,
    desiredSkills: t.skills,

    // B2C
    topics: t.topics,

    // Location
    cities: t.cities,
    locationRespondentInPerson: t.locationInPerson,

    // NDA
    ndaType: t.ndaType ?? undefined,
    ndaLink: t.ndaLink || undefined,
    ndaFileUrl: t.ndaFileUrl || undefined,

    // Past-participant filtering + qualifiers
    filtering: t.filtering,
    keyQualifiers: t.keyQualifiers,

    // Advanced targeting (flat keyed map)
    advancedTargeting: t.advancedTargeting,
  };
}

// ─── Page Component ──────────────────────────────────────────────────────────

function EditProjectPage() {
  const { projectId } = Route.useParams();
  const isNewProject = projectId === 'new';
  const location = useLocation();
  const navState = location.state as unknown as ProjectCreateNavState | null;
  const initialPrompt = navState?.prompt ?? '';
  const initialBriefFileName = navState?.briefFileName;
  /**
   * Program this project should join, when creation was started from one.
   *
   * Captured once on mount rather than read at use time: creating the project
   * navigates to the real id with `replace`, which drops the funnel's router
   * state, so by the time anything downstream looked again it would be gone.
   */
  const programIdRef = useRef(isNewProject ? navState?.programId : undefined);
  const newProjectSeed = useMemo(
    () => (isNewProject ? buildNewProjectSeed(navState) : null),
    [isNewProject, navState],
  );
  // Existing drafts are always AI flow; new projects honor the funnel's flow
  // (defaulting to manual for the legacy "Start from scratch" path).
  const initialFlow = resolveInitialFlow(navState, isNewProject);

  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const analytics = useAnalytics();
  const previewDefaultOpen = useBreakpoint('3xl');

  // The `__root__` context can still report a null team on the single-team
  // auto-resolution path (the `projects` layout `beforeLoad` resolves it into
  // its own context, not the root session, until the next reload). Read the
  // resolved dashboard here so the team is scoped to what the current session
  // is actually authorized for — deriving it from the loaded project could
  // surface another team's questions if the user lost access.
  const {
    user: authUser,
    activeDashboard: { team: teamId, organization: dashboardOrgId },
  } = useRouteContext({ from: '/researchers/_authenticated/projects' });
  const { projectOrganizationId } = Route.useRouteContext();

  // ── Organization AI policy ──
  // Every AI entry point in the editor hangs off `canUseAi`: the chat pane
  // and its toggle, the initial prompt / brief send, AI edit toolbars and
  // "Generate with AI", flag fixes, AI study checks, AI media notes. It is
  // `false` until the organization has settled; the route's `beforeLoad`
  // prefetches the project's organization, so on first render the answer is
  // already known and the chat panel mounts in its final state rather than
  // opening and then snapping shut. Keyed on the PROJECT's organization (the
  // dashboard's for a new draft), the same one the flag reads below use.
  const policyOrgId = projectOrganizationId ?? dashboardOrgId;
  const { data: canUseAi = false } = useQuery(
    organizationCanUseAiQueryOptions(policyOrgId),
  );

  // ── Panel state ──
  // The chat is the AI assistant: it starts (and stays) collapsed when the
  // organization cannot use AI, whatever the funnel's flow.
  const [isChatCollapsed, setIsChatCollapsed] = useState(
    initialFlow === 'manual' || !canUseAi,
  );
  // Captured once: `defaultSize` must stay static so toggling `isChatCollapsed`
  // mid-tween doesn't re-register the panel and recompute the layout (which
  // would fight the size animation).
  const initialChatCollapsed = useRef(
    initialFlow === 'manual' || !canUseAi,
  ).current;
  const [isPreviewCollapsed, setIsPreviewCollapsed] = useState(true);
  // Animated collapse/expand for the side panels. The hook owns the panel ref,
  // owns the panel ref and drives a single imperative collapse()/resize(); the
  // smooth glide comes from a standing CSS flex-grow transition on the Group's
  // panels (see the Group className below), not from React.
  const { panelRef: chatPanelRef, setCollapsed: setChatCollapsed } =
    useAnimatedPanel('30%');
  const { panelRef: previewPanelRef, setCollapsed: setPreviewCollapsed } =
    useAnimatedPanel('30%');

  // ── Drawers ──
  const [isTargetingDrawerOpen, setIsTargetingDrawerOpen] = useState(false);
  const [isStudyTypeDrawerOpen, setIsStudyTypeDrawerOpen] = useState(false);

  // ── Preview ──
  // One-shot, nonce-keyed request to jump the preview to a step (and, for the
  // screener / interview steps, a specific question). The nonce makes each
  // play-button click a fire-once event the preview reacts to.
  const [previewJump, setPreviewJump] = useState<PreviewJump>();
  const previewJumpNonceRef = useRef(0);
  const [spotlightRequest, setSpotlightRequest] =
    useState<AiProjectSpotlightRequest | null>(null);

  // ── Session & feature flags ──
  const { data: session } = useQuery(getCurrentSessionOptions);
  const googleApiKey = session?.env.GOOGLE_BROWSER_APIKEY;

  const addProjectToProgram = useAddProgramProject(teamId ?? '', authUser.id);

  // ── 1. Reducer state ──
  const historyReducer = useMemo(() => withHistory(aiProjectReducer), []);
  const [state, dispatch] = useReducer(historyReducer, {
    ...initialAiProjectState,
    projectId: isNewProject ? null : projectId,
    kindOfResearch: newProjectSeed?.kindOfResearch ?? null,
    researchConductType: newProjectSeed?.researchConductType ?? null,
    interviewDeliveryMode: newProjectSeed?.interviewDeliveryMode ?? null,
    studyLanguage: newProjectSeed?.studyLanguage ?? null,
    responseLanguages: newProjectSeed?.responseLanguages ?? [],
    _history: createHistoryMeta(100),
  } as StateWithHistory);

  // Reports manual edits to the draft (what + when) to PostHog. Keyed off the
  // persisted draft id (`state.projectId`), which is null until the first save —
  // never the route param, which stays 'new' until the URL is replaced.
  const recordEdit = useAiCreateEditTelemetry(state.projectId, analytics);

  // ── Project data ──
  const { data: project } = useQuery({
    ...researcherProjectQueryOptions(state.projectId ?? projectId),
    enabled: !isNewProject,
    select: (res) => {
      if (!res.success) return null;
      return res.data;
    },
  });

  const effectiveOrgId = project?.organizationId ?? policyOrgId;
  // Selecting AI moderation needs the existing org flag AND no AI opt-out.
  // Study-type availability (step order for an existing AI-mod draft) follows
  // the flag alone; offering AI moderation as a choice needs both gates.
  const [aiModFlag, effectiveOrgAiPolicy] = useQueries({
    queries: [
      orgFeatureFlagQueryOptions(effectiveOrgId, AI_MODERATED_INTERVIEWS_FLAG),
      organizationCanUseAiQueryOptions(effectiveOrgId),
    ],
  });
  const isAiModEnabled = aiModFlag.data === true;
  const canSelectAiModeration =
    isAiModEnabled && effectiveOrgAiPolicy.data === true;

  const { data: hasIncreasedTargetLimitEnabled } = useQuery({
    ...organizationQueryOptions(effectiveOrgId ?? ''),
    enabled: !!effectiveOrgId,
    select: (org) => org?.features?.increasedTargetLimit === true,
  });
  // ── Screener question limit ──
  const { data: isScreenerLimitEnabled = false } = useQuery(
    orgFeatureFlagQueryOptions(effectiveOrgId, SCREENER_LIMIT_FEATURE_FLAG),
  );
  const screenerMaxQuestions = isScreenerLimitEnabled
    ? SCREENER_LIMITS.MAX_QUESTIONS_HIGH
    : SCREENER_LIMITS.MAX_QUESTIONS_DEFAULT;
  // PX-453: an organization on the allow-zero flag may launch with no
  // screener; the server applies the same flag at publish.
  const { data: allowZeroScreenerQuestions = false } = useQuery(
    orgFeatureFlagQueryOptions(
      effectiveOrgId,
      ALLOW_ZERO_SCREENER_QUESTIONS_FLAG,
    ),
  );
  const screenerMinQuestions = resolveScreenerMinQuestions(
    allowZeroScreenerQuestions,
  );
  // PX-456: an organization on the optional-topics flag may launch a B2B
  // project with no industries or a B2C project with no topics once the
  // researcher has picked the audience type. Same rule the target-audience
  // card renders with, so the Review gate cannot contradict it.
  const { data: allowOptionalTopics = false } = useQuery(
    orgFeatureFlagQueryOptions(effectiveOrgId, ALLOW_OPTIONAL_TOPICS_FLAG),
  );
  const openAudiencePicked =
    allowOptionalTopics && !!project && hasPickedAudience(project);

  // Participant sources (the researcher's own participants): on the flag
  // and for the study kinds the invite link admits to, the sources section
  // replaces the Target audience card, carrying its chips in the panel card.
  const { data: isExternalParticipantsFlagOn = false } = useQuery(
    orgFeatureFlagQueryOptions(
      effectiveOrgId,
      EXTERNAL_PARTICIPANT_INVITES_FLAG,
    ),
  );
  const showParticipantSources =
    isExternalParticipantsFlagOn &&
    supportsExternalParticipants(state.kindOfResearch);

  const { data: isFileUploadQuestionEnabled = false } = useQuery(
    orgFeatureFlagQueryOptions(
      effectiveOrgId,
      ENABLE_SCREENER_FILE_UPLOAD_QUESTION,
    ),
  );
  // New/draft projects don't have a loaded `project` yet — fall back to the
  // session's active dashboard org so the flag resolves for the draft flow
  // and the RANK type appears in the dropdown before the project is saved.
  const { data: rankQuestionEnabled = false } = useQuery(
    orgFeatureFlagQueryOptions(effectiveOrgId, RANK_QUESTION_FEATURE_FLAG),
  );
  const { data: matrixQuestionEnabled = false } = useQuery(
    orgFeatureFlagQueryOptions(effectiveOrgId, MATRIX_QUESTION_FEATURE_FLAG),
  );
  // The org rollout flag only. Named apart from the participant API's
  // `screenRecordingEnabled`, which means the org flag AND the script asking
  // for it. One flag covers usability testing and the screen recording a task
  // rides on; there used to be a separate `screen-recording` query here.
  const { data: usabilityTestingEnabled = false } = useQuery(
    orgFeatureFlagQueryOptions(effectiveOrgId, 'usability-testing'),
  );
  // Recording panel layout for the preview: the same org flag the participant
  // payload reads, so what the researcher previews is what ships.
  const { data: recordingPanelFlag = false } = useQuery(
    orgFeatureFlagQueryOptions(effectiveOrgId, 'recording-panel'),
  );
  const previewRecordingPanel = previewRecordingPanelEnabled({
    usabilityTesting: usabilityTestingEnabled,
    recordingPanelFlag,
    interviewDeliveryMode: state.interviewDeliveryMode,
    interviewSections: state.interviewSections,
  });

  // One-time announcement for the three advanced question types. Gated on its
  // own flag rather than on the type flags: having the types and being told
  // about them are separate rollout decisions.
  const { data: isAdvancedScreenerAnnouncementEnabled = false } = useQuery(
    orgFeatureFlagQueryOptions(
      effectiveOrgId,
      ADVANCED_SCREENER_ANNOUNCEMENT_FLAG,
    ),
  );
  const advancedScreenerAnnouncement = useAdvancedScreenerAnnouncement(
    isAdvancedScreenerAnnouncementEnabled,
  );
  // Off while loading, so a researcher without the flag never sees the checks
  // drawer flash in or triggers a check. The checks themselves are an AI call
  // (not just their "Fix with AI" buttons), so an AI opt-out turns them off
  // too; launch blockers fall back to the plain validation list.
  const studyChecksEnabled =
    useFeatureFlag(STUDY_CHECKS_FEATURE_FLAG).isEnabled && canUseAi;

  // ── Panel toggle helpers ──
  // Drive the resizable panel imperatively and keep the local collapsed flag in
  // sync. Dragging the panel below its min size triggers `onResize` (below),
  // which reconciles the flag when the user collapses/expands by dragging.
  const toggleChat = useCallback(
    (collapsed: boolean = !isChatCollapsed) => {
      // Start the imperative collapse()/resize() + CSS glide immediately, but
      // defer the `isChatCollapsed` flag: it feeds `isChatOpen` into the heavy
      // editor subtree, whose synchronous re-render (~370ms) otherwise blocks
      // the main thread and starves the flex-grow transition. As a transition,
      // React yields to the animation and renders the subtree off the hot path.
      setChatCollapsed(collapsed);
      startTransition(() => setIsChatCollapsed(collapsed));
    },
    [isChatCollapsed, setChatCollapsed],
  );

  // Keep the assistant shut if the organization's AI access is withdrawn
  // while the editor is open (e.g. the policy settles late for a project in
  // another organization). Opening it is only offered while `canUseAi`.
  useEffect(() => {
    if (!canUseAi && !isChatCollapsed) toggleChat(true);
  }, [canUseAi, isChatCollapsed, toggleChat]);

  const togglePreview = useCallback(
    (collapsed: boolean = !isPreviewCollapsed) => {
      setPreviewCollapsed(collapsed);
      startTransition(() => setIsPreviewCollapsed(collapsed));
    },
    [isPreviewCollapsed, setPreviewCollapsed],
  );

  // ── Refs ──
  const hydratedRef = useRef(false);
  // State twin of hydratedRef: consumers (the AI chat welcome-back gate) need
  // a reactive signal that the reducer now holds the persisted project.
  const [isHydrated, setIsHydrated] = useState(false);

  const stateRef = useRef(state);
  useEffect(() => {
    stateRef.current = state;
  }, [state]);
  /**
   * The draft as it stood before the AI call now completing. A streamed
   * preview writes into live state with placeholder uids and partial text, so
   * a completed call must apply against the snapshot the history wrapper took
   * at the first frame, never against what is currently painted.
   */
  const preStreamState = useCallback((): typeof state => {
    const current = stateRef.current;
    const snapshot = current._history.streamSnapshot;
    return snapshot ? { ...current, ...snapshot } : current;
  }, []);

  const screenerQuestionsRef = useRef(state.screenerQuestions);
  useEffect(() => {
    screenerQuestionsRef.current = state.screenerQuestions;
  }, [state.screenerQuestions]);

  // ONCALL-2139: the screenerVersion of the state THIS EDITOR was built from.
  // Captured at hydrate and advanced only from save responses — deliberately
  // NOT read from the query cache, which background refetches (focus/
  // reconnect) move forward independently of the reducer: a stale editor
  // would then send a "fresh" version and silently overwrite another
  // session's screener. Sent with every screener save so the server can 409
  // when the array moved underneath this editor.
  const screenerBasisVersionRef = useRef(0);

  // ONCALL-2139: a save 409'd — another session changed the screener since
  // this editor hydrated. While set, the save queue is gated (nothing reaches
  // the server, so one conflict never becomes a 409 per keystroke) and the
  // header shows the recovery choices. Editing stays enabled on purpose: the
  // researcher may want to copy their in-progress work out before resolving.
  const [screenerConflict, setScreenerConflict] = useState(false);
  const screenerConflictRef = useRef(false);
  useEffect(() => {
    screenerConflictRef.current = screenerConflict;
  }, [screenerConflict]);

  // Latest sections for the streaming preview callback (a ref, so the memoized
  // callbacks object doesn't churn on every sections change).
  const requestSpotlight = useCallback((target: AiProjectSpotlightTarget) => {
    setSpotlightRequest({ id: crypto.randomUUID(), target });
  }, []);

  // ── Mock project for draft mode ──
  // For drafts, we build a mock project from reducer state so the drawer can work
  // Deps narrowed to the slices buildMockProjectForDraft reads so streaming
  // dispatches don't churn the drawer props.
  // Organization and team come from the resolved dashboard (see the
  // `useRouteContext` note above), not the raw session, so the drawer's
  // per-organization flag reads (advanced targeting, optional topics) have an
  // id on a brand-new draft too.
  const draftProject = useMemo(
    () => buildMockProjectForDraft(state, dashboardOrgId, teamId),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      state.targeting,
      state.kindOfResearch,
      state.researchConductType,
      state.duration,
      state.interviewDeliveryMode,
      state.studyLanguage,
      dashboardOrgId,
      teamId,
    ],
  );

  const recruitFromPanel = state.targeting.recruitFromPanel !== false;
  // The researcher's own participants live in the reducer's targeting next
  // to the panel switch, so a source change by hand or by the AI has an undo
  // entry and saves with targeting. Seeded from the saved project once per
  // project while the Participant sources section is on the canvas, and
  // cleared when the section goes: the server refuses the field for other
  // study kinds and unflagged organizations, so it must not ride along then.
  const ownParticipants = state.targeting.ownParticipants;
  const seededOwnParticipantsFor = useRef<string | null | undefined>(undefined);
  useEffect(() => {
    const current = stateRef.current.targeting.ownParticipants;
    if (!showParticipantSources) {
      seededOwnParticipantsFor.current = undefined;
      if (current !== undefined) {
        dispatch({ type: 'SET_OWN_PARTICIPANTS', ownParticipants: undefined });
      }
      return;
    }
    const key = project?.id ?? null;
    if (seededOwnParticipantsFor.current === key && current !== undefined) {
      return;
    }
    seededOwnParticipantsFor.current = key;
    const stored = externalParticipantsFromProject(project ?? undefined);
    dispatch({
      type: 'SET_OWN_PARTICIPANTS',
      // Off zeroes the stored target; keep the count the canvas had so
      // turning back on offers it.
      ownParticipants: {
        ...stored,
        target:
          project?.recruitmentTargets?.externalParticipants ||
          current?.target ||
          stored.target,
      },
    });
  }, [showParticipantSources, project]);
  const externalParticipantsOn = ownParticipants?.enabled === true;
  const externalSkipsScreener =
    externalParticipantsOn && ownParticipants?.skipScreener === true;
  const showScreener = recruitFromPanel || !externalSkipsScreener;
  const stepOrder = useMemo(
    () =>
      getStepOrder(isAiModEnabled, state.kindOfResearch, {
        recruitFromPanel,
        showScreener,
      }),
    [isAiModEnabled, state.kindOfResearch, recruitFromPanel, showScreener],
  );

  // Deps narrowed to only the fields getStepCompletion reads (see -step-completion.ts)
  // so that unrelated state changes (saveStatus, currentStep, projectId) don't recompute.
  const stepCompletion = useMemo(
    () =>
      getStepCompletion(state, stepOrder, {
        minScreenerQuestions: screenerMinQuestions,
        openAudiencePicked,
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      screenerMinQuestions,
      openAudiencePicked,
      state.outline,
      state.targeting,
      state.screenerQuestions,
      state.welcomeScreen,
      state.closingMessage,
      state.interviewSections,
      state.kindOfResearch,
      stepOrder,
    ],
  );

  const handleInitialPromptConsumed = useCallback(() => {
    void navigate({
      to: '.',
      replace: true,
      state: { flow: initialFlow } as Record<string, unknown>,
    });
  }, [navigate, initialFlow]);

  const handleRestoredStep = useCallback((step: AiProjectStep) => {
    dispatch({ type: 'SET_STEP', step });
  }, []);

  const handleUserMessageSent = useCallback(
    (text: string) => {
      // Toolbar actions send synthetic hidden prompts through the same path.
      // Activity-backed prompts carry a visible provenance marker before the
      // system instruction, but neither is a conversational follow-up.
      if (text.startsWith('[System:') || parseChatActivity(text)) return;
      // Conversational refinement loop. Never log the message content —
      // only its length.
      analytics?.track({
        action: 'submitted',
        actionSubject: 'creationFollowup',
        attributes: {
          projectId,
          step: state.currentStep,
          message_length: text.length,
        },
      });
    },
    [analytics, projectId, state.currentStep],
  );

  const {
    messages,
    setMessages,
    status,
    isLoading,
    restoredMessages,
    restoredToolIds,
    lastSessionTime,
    isRestoring,
    handleSend,
    handleStop,
    stoppedMessageIds,
  } = useAiProjectChat({
    projectId,
    isNewProject,
    isHydrated,
    isAiEnabled: canUseAi,
    state,
    stepOrder,
    stepCompletion,
    minScreenerQuestions: screenerMinQuestions,
    participantSources: showParticipantSources,
    initialPrompt,
    initialBriefFileName,
    onInitialPromptConsumed: handleInitialPromptConsumed,
    onRestoredStep: handleRestoredStep,
    onUserMessageSent: handleUserMessageSent,
  });

  // ── Screen event ──
  const screenFiredRef = useRef(false);
  useEffect(() => {
    if (isRestoring || screenFiredRef.current) return;
    screenFiredRef.current = true;
    analytics?.screen({
      name: 'projectEditor',
      attributes: {
        projectId,
        flow: initialFlow,
        hasRestoredSession:
          restoredMessages !== null && restoredMessages.length > 0,
      },
    });
  }, [isRestoring, analytics, projectId, restoredMessages, initialFlow]);

  const handleProjectCreated = useCallback(
    (nextProjectId: string) => {
      dispatch({ type: 'SET_PROJECT_ID', projectId: nextProjectId });

      // Creation started from a program: this is the first moment the project
      // exists, so it is the earliest point membership can be recorded.
      //
      // Deliberately not awaited and deliberately not blocking the navigation.
      // The project is already saved; if this add fails the researcher has a
      // perfectly good project that simply is not grouped yet, which they can
      // fix from the program's "Add existing project". Holding up the editor —
      // or worse, surfacing an error that reads as though the save failed —
      // would be a worse trade than an ungrouped project.
      if (programIdRef.current && teamId) {
        addProjectToProgram.mutate({
          programId: programIdRef.current,
          projectId: nextProjectId,
        });
      }

      void navigate({
        to: '/researchers/projects/$projectId/edit',
        params: { projectId: nextProjectId },
        replace: true,
      });
    },
    [addProjectToProgram, navigate, teamId],
  );

  const getScreenerVersion = useCallback(
    () => screenerBasisVersionRef.current,
    [],
  );
  const handleDraftSaved = useCallback(
    (saved: Project, savedFields: DraftSaveFields) => {
      // Only screener-carrying saves advance the basis. A non-screener save
      // (title, outline, …) is not version-pinned, so its response can carry
      // a screenerVersion another session just bumped — adopting it without
      // adopting that session's questions would let this editor's next
      // screener save clobber theirs without a 409. For screener saves, the
      // server is the source of truth for version transitions.
      if (savedFields.screenerQuestions == null) return;
      if (saved.screenerVersion != null) {
        screenerBasisVersionRef.current = saved.screenerVersion;
      }
    },
    [],
  );
  const handleSaveConflict = useCallback(() => {
    screenerConflictRef.current = true;
    setScreenerConflict(true);
  }, []);
  const isSavePaused = useCallback(() => screenerConflictRef.current, []);

  const {
    saveStatus,
    lastError,
    scheduleSave,
    flushSave,
    takePending,
    markSynced,
    hasPending,
    isBusy,
  } = useDraftAutoSave({
    projectId: state.projectId,
    teamId: teamId,
    onProjectCreated: handleProjectCreated,
    getScreenerVersion,
    onSaved: handleDraftSaved,
    onConflict: handleSaveConflict,
    filterPayload: filterOverLimitAiModFields,
    isPaused: isSavePaused,
  });

  // ── Conflict recovery (ONCALL-2139) ──
  // Both paths re-read the server first; neither destroys local work:
  // "Get latest" replaces the canvas via a TRACKED history entry (the
  // researcher's edits are one undo away), "Overwrite" pushes the local
  // state over the other session's under the fresh version.
  const fetchFreshProject = useCallback(async (): Promise<Project | null> => {
    const id = state.projectId;
    if (!id) return null;
    // Realtime/conflict recovery must bypass the app-wide one-minute
    // staleTime. Otherwise the first Pusher event refreshes the cache and
    // subsequent events within that minute keep returning that first result.
    const res = await queryClient.fetchQuery({
      ...researcherProjectQueryOptions(id),
      staleTime: 0,
    });
    return res.success ? res.data : null;
  }, [queryClient, state.projectId]);

  const handleConflictGetLatest = useCallback(async () => {
    const fresh = await fetchFreshProject();
    if (!fresh) return;
    // The pending screener payload predates the resync — flushing it later
    // would overwrite the freshly synced state with pre-conflict edits. But
    // only the screener conflicted: unrelated buffered edits (title,
    // description, …) are kept and flushed below, not thrown away.
    const pending = takePending();
    const nonScreenerPending = { ...pending };
    delete nonScreenerPending.screenerQuestions;
    // Tracked on purpose (unlike HYDRATE_FROM_PROJECT): the resync becomes an
    // undoable history entry, so "my edits before sync" are one undo away.
    dispatch({
      type: 'HISTORY_BATCH',
      label: 'Get latest version',
      actions: [
        {
          type: 'SET_SCREENER',
          payload: fresh.screenerQuestions
            ? apiQuestionsToForm(fresh.screenerQuestions)
            : [],
        },
      ],
      spotlight: { section: 'screener' },
    });
    screenerBasisVersionRef.current = fresh.screenerVersion ?? 0;
    screenerConflictRef.current = false;
    setScreenerConflict(false);
    if (Object.keys(nonScreenerPending).length > 0) {
      // The surviving non-screener edits still need to reach the server —
      // this save runs under the freshly adopted basis and moves the status
      // itself, so no markSynced here.
      void flushSave(nonScreenerPending);
      return;
    }
    // The 409'd save left the hook's status at 'error' (rendered as "Unsaved
    // changes"), but the editor now exactly matches the server — say so.
    // The overwrite path needs no equivalent: its flushSave runs a real save
    // that moves the status itself.
    markSynced();
  }, [fetchFreshProject, takePending, flushSave, markSynced]);

  const handleConflictOverwrite = useCallback(async () => {
    const fresh = await fetchFreshProject();
    if (!fresh) return;
    // Adopt the server's current version as the new basis, then push this
    // editor's screener over it — an informed overwrite, not a silent one.
    // A third session saving in between simply re-enters conflict.
    screenerBasisVersionRef.current = fresh.screenerVersion ?? 0;
    screenerConflictRef.current = false;
    setScreenerConflict(false);
    void flushSave(buildScreenerPayload(screenerQuestionsRef.current));
  }, [fetchFreshProject, flushSave]);

  // ── Realtime screener sync (ONCALL-2139) ──
  // Another session's draft save broadcasts `screener.updated`; this tab
  // refetches and compares versions instead of waiting for its own next save
  // to 409. Clean editor → adopt silently (tracked, so undo still works);
  // anything unsent or in flight → raise the conflict banner proactively —
  // never yank a canvas someone is typing into.
  const saveStatusRef = useRef(saveStatus);
  useEffect(() => {
    saveStatusRef.current = saveStatus;
  }, [saveStatus]);

  const handleScreenerRealtimeUpdate = useCallback(async () => {
    // Function reads (not direct property access): the ref mutates across the
    // await below, which TypeScript's narrowing would otherwise reason away.
    // `isBusy` (set synchronously at enqueue) covers the window where a
    // payload has left the debounce buffer but `saveStatus` hasn't rendered
    // 'saving' yet — adopting in that window would advance the basis and let
    // the queued stale screener save successfully under it.
    const isSaving = () => isBusy() || saveStatusRef.current === 'saving';

    // Already in conflict: the banner owns recovery.
    if (screenerConflictRef.current) return;
    // Our own save queued or in flight: its outcome settles the question —
    // success advances the basis (making the echo a no-op), failure raises
    // conflict.
    if (isSaving()) return;

    const fresh = await fetchFreshProject();
    if (!fresh) return;
    // Our own write's echo, or a stale/duplicate event.
    if ((fresh.screenerVersion ?? 0) <= screenerBasisVersionRef.current) return;

    // Re-check the gates after the await: a save may have started or a
    // conflict may have been raised while the fetch was in flight.
    if (screenerConflictRef.current || isSaving()) {
      return;
    }

    const isClean =
      !hasPending() &&
      (saveStatusRef.current === 'saved' || saveStatusRef.current === 'idle');

    if (!isClean) {
      // Unsent local edits exist — adopting the server's version now would
      // let their eventual flush clobber the other session's write. Surface
      // the existing conflict UI instead (saves gate immediately).
      screenerConflictRef.current = true;
      setScreenerConflict(true);
      return;
    }

    dispatch({
      type: 'HISTORY_BATCH',
      label: 'Synced from another session',
      actions: [
        {
          type: 'SET_SCREENER',
          payload: fresh.screenerQuestions
            ? apiQuestionsToForm(fresh.screenerQuestions)
            : [],
        },
      ],
      spotlight: { section: 'screener' },
    });
    screenerBasisVersionRef.current = fresh.screenerVersion ?? 0;
    markSynced();
  }, [fetchFreshProject, hasPending, isBusy, markSynced]);

  useScreenerRealtime({
    appKey: session?.env.PUSHER_APP_KEY,
    projectId: state.projectId,
    onScreenerUpdated: handleScreenerRealtimeUpdate,
  });

  // A rejected save leaves the editor showing content the server never stored,
  // so the researcher has to be told: the header status alone reads as merely
  // "not saved yet" and they will keep working on content that is already lost.
  const notifiedErrorRef = useRef<string | null>(null);
  useEffect(() => {
    if (saveStatus !== 'error') {
      notifiedErrorRef.current = null;
      return;
    }
    if (!lastError || notifiedErrorRef.current === lastError) return;
    notifiedErrorRef.current = lastError;
    notify({
      color: 'error',
      title: t`Your changes were not saved`,
      description: lastError,
    });
  }, [saveStatus, lastError]);

  // ── 1b. Hydrate state from cached project ──
  useEffect(() => {
    if (hydratedRef.current) return;

    if (isNewProject) {
      hydratedRef.current = true;
      setIsHydrated(true);

      // Seed study type / medium / language captured by the creation funnel so
      // the editor reflects the researcher's choice before the first save.
      const seed = newProjectSeed ?? buildNewProjectSeed(navState);
      if (seed.kindOfResearch != null) {
        dispatch({
          type: 'SET_KIND_OF_RESEARCH',
          kindOfResearch: seed.kindOfResearch,
        });
      }
      if (seed.researchConductType != null) {
        dispatch({
          type: 'SET_RESEARCH_CONDUCT_TYPE',
          researchConductType: seed.researchConductType,
        });
      }
      if (seed.interviewDeliveryMode != null) {
        dispatch({
          type: 'SET_INTERVIEW_DELIVERY_MODE',
          interviewDeliveryMode: seed.interviewDeliveryMode,
        });
      }
      if (seed.studyLanguage != null) {
        dispatch({
          type: 'SET_STUDY_LANGUAGE',
          studyLanguage: seed.studyLanguage,
        });
      }
      if (seed.responseLanguages != null && seed.responseLanguages.length > 0) {
        dispatch({
          type: 'SET_RESPONSE_LANGUAGES',
          responseLanguages: seed.responseLanguages,
        });
      }

      // Persist the seeded study type. The reducer dispatches above only update
      // local state; without this, kindOfResearch/etc. are never saved unless
      // the user manually re-picks the project type (the AI never calls
      // setProjectType for a fresh project, and core-api's create() omits it).
      // Debounced so we don't create a project the instant the editor loads.
      const seedPayload: Partial<UpdateResearcherDraftDto> = {
        ...(seed.kindOfResearch != null && {
          kindOfResearch: seed.kindOfResearch,
        }),
        ...(seed.researchConductType != null && {
          researchConductType: seed.researchConductType,
        }),
        ...(seed.interviewDeliveryMode != null && {
          interviewDeliveryMode: seed.interviewDeliveryMode,
        }),
        ...(seed.studyLanguage != null && {
          studyLanguage: seed.studyLanguage,
        }),
        ...(seed.responseLanguages != null &&
          seed.responseLanguages.length > 0 && {
            responseLanguages: seed.responseLanguages,
          }),
      };
      if (Object.keys(seedPayload).length > 0) {
        scheduleSave(seedPayload);
      }

      const allSteps = getStepOrder(
        isAiModEnabled,
        seed.kindOfResearch ?? null,
      );
      dispatch({ type: 'SET_COMPLETED_STEPS', steps: allSteps });
      return;
    }

    if (!project) return;
    hydratedRef.current = true;
    setIsHydrated(true);

    // The screener basis: the version of the array the reducer is about to
    // be built from (ONCALL-2139).
    screenerBasisVersionRef.current = project.screenerVersion ?? 0;

    dispatch({
      type: 'HYDRATE_FROM_PROJECT',
      outline: {
        name: project.name || '',
        internalName: project.internalName || '',
        description: project.description || '',
        studyGoals: project.studyGoals || '',
      },
      targeting: buildTargetingFromProject(project),
      screenerQuestions: project.screenerQuestions
        ? apiQuestionsToForm(project.screenerQuestions)
        : [],
      kindOfResearch: project.kindOfResearch ?? null,
      researchConductType: project.researchConductType ?? null,
      duration: project.duration ?? null,
      interviewDeliveryMode: project.interviewDeliveryMode ?? null,
      studyLanguage: isStudyLanguage(project.studyLanguage)
        ? project.studyLanguage
        : null,
      responseLanguages: (project.responseLanguages ?? []).filter((code) =>
        isStudyLanguage(code),
      ),
      welcomeScreen: project.welcomeScreen
        ? {
            title: project.welcomeScreen.title || '',
            message: project.welcomeScreen.message || '',
          }
        : undefined,
      closingMessage: project.closingMessage
        ? {
            closingMessage: project.closingMessage.closingMessage || '',
          }
        : undefined,
      interviewSections:
        project.interviewSections && project.interviewSections.length > 0
          ? interviewSectionsFromProject(project.interviewSections)
          : undefined,
    });
  }, [
    project,
    isAiModEnabled,
    isNewProject,
    navState,
    newProjectSeed,
    scheduleSave,
  ]);

  useEffect(() => {
    dispatch({ type: 'SET_SAVE_STATUS', status: saveStatus });
  }, [saveStatus]);

  useStepCompletionAnalytics(state.projectId, stepCompletion.status);

  const applyAiAction = useCallback(
    (
      action: AiProjectAction,
      savePayload: Partial<UpdateResearcherDraftDto>,
    ) => {
      const spotlight = action.spotlight;
      dispatch({ ...action, historyOrigin: 'ai' });
      if (spotlight) requestSpotlight(spotlight);
      void flushSave(savePayload);
    },
    [flushSave, requestSpotlight],
  );

  const applyAiBatch = useCallback(
    (
      label: string,
      actions: AiProjectAction[],
      spotlight: AiProjectSpotlightTarget,
      savePayload: Partial<UpdateResearcherDraftDto>,
    ) => {
      dispatch({
        type: 'HISTORY_BATCH',
        label,
        actions,
        spotlight,
        origin: 'ai',
      });
      requestSpotlight(spotlight);
      void flushSave(savePayload);
    },
    [flushSave, requestSpotlight],
  );

  const applyHistoryEntry = useCallback(
    (
      direction: 'undo' | 'redo',
      entry: StateWithHistory['_history']['past'][number] | undefined,
    ) => {
      if (!entry) return;

      dispatch({
        type: direction === 'undo' ? 'HISTORY_UNDO' : 'HISTORY_REDO',
      });

      if (entry.spotlight) requestSpotlight(entry.spotlight);

      void flushSave(
        buildTrackedStatePatch(
          direction === 'undo' ? entry.before : entry.after,
          direction === 'undo' ? entry.after : entry.before,
        ),
      );
    },
    [flushSave, requestSpotlight],
  );

  const buildProjectTypeHistory = useCallback(
    (
      oldKor: KindOfResearch | null,
      newKor: KindOfResearch,
      newRct: ResearchConductType,
      cappedCount: number,
      prevCount: number,
    ) => {
      const actions: AiProjectAction[] = [
        { type: 'SET_KIND_OF_RESEARCH', kindOfResearch: newKor },
        { type: 'SET_RESEARCH_CONDUCT_TYPE', researchConductType: newRct },
      ];

      if (isAiModStudy(oldKor) && !isAiModStudy(newKor)) {
        actions.push({ type: 'CLEAR_AI_MOD_FIELDS' });
      } else if (!isAiModStudy(oldKor) && isAiModStudy(newKor)) {
        actions.push({
          type: 'SET_INTERVIEW_DELIVERY_MODE',
          interviewDeliveryMode: 'video',
        });
        actions.push({
          type: 'SET_STUDY_LANGUAGE',
          studyLanguage: DEFAULT_STUDY_LANGUAGE,
        });
      }

      if (prevCount > cappedCount) {
        actions.push({
          type: 'PATCH_TARGETING',
          field: 'numberOfParticipants',
          value: cappedCount,
        });
      }

      return actions;
    },
    [],
  );

  // ── 4. Tool call callbacks ──
  const handleOutlinePatch = useCallback(
    (patch: AiOutlinePatch) => {
      const { actions, next, changed } = outlineChanges(
        patch,
        preStreamState().outline,
      );
      if (changed.includes('studyGoals')) {
        // First AI deliverable. The project goal is the defining piece of the
        // drafted outline, so treat its arrival as "outline generated".
        analytics?.track({
          action: 'generated',
          actionSubject: 'studyOutline',
          attributes: { projectId, success: true },
        });
        dispatch({ type: 'COMPLETE_STEP', step: 'outline' });
      }
      if (actions.length === 0) return;
      const spotlight: AiProjectSpotlightTarget =
        changed.length === 1
          ? { section: 'outline', field: changed[0] }
          : { section: 'outline' };
      applyAiBatch(
        'AI: Update outline',
        actions,
        spotlight,
        buildOutlinePayload(next),
      );
    },
    [analytics, projectId, applyAiBatch, preStreamState],
  );

  const handleTargetingPatch = useCallback(
    (patch: AiTargetingPatch) => {
      const max = getParticipantTargetLimit(
        stateRef.current.kindOfResearch,
        hasIncreasedTargetLimitEnabled === true,
      );
      const { actions, next, changed } = targetingChanges(
        patch,
        preStreamState().targeting,
        max,
      );
      if (actions.length === 0) return;
      dispatch({ type: 'COMPLETE_STEP', step: 'targeting' });
      const surfaces = [
        ...new Set(changed.map((field) => TARGETING_SPOTLIGHT[field] ?? field)),
      ];
      const spotlight: AiProjectSpotlightTarget =
        surfaces.length === 1
          ? { section: 'targeting', field: surfaces[0] }
          : { section: 'targeting' };
      applyAiBatch(
        'AI: Update targeting',
        actions,
        spotlight,
        buildTargetingPayload(next),
      );
    },
    [applyAiBatch, preStreamState, hasIncreasedTargetLimitEnabled],
  );

  /**
   * Handles clearing type-specific state + backend fields when the project type
   * changes. Used by the AI tool call handler, the card picker, and the drawer.
   */
  const applyProjectTypeTransition = useCallback(
    (
      oldKor: KindOfResearch | null | undefined,
      newKor: KindOfResearch,
      extraSaveFields?: DraftSaveFields,
      historyOrigin: 'user' | 'ai' = 'user',
    ) => {
      const shouldClearSurveySettings =
        shouldResetParticipantEmailsForProjectTypeChange(oldKor, newKor);

      // AI mod → non-AI mod: clear interview, welcome, closing
      if (isAiModStudy(oldKor) && !isAiModStudy(newKor)) {
        dispatch({ type: 'CLEAR_AI_MOD_FIELDS', historyOrigin });
        void flushSave({
          ...extraSaveFields,
          welcomeScreen: {} as UpdateResearcherDraftDto['welcomeScreen'],
          closingMessage: {} as UpdateResearcherDraftDto['closingMessage'],
          interviewSections: [],
          interviewDeliveryMode: null,
          studyLanguage: null,
          responseLanguages: null,
        });
      }
      // Non-AI mod → AI mod: set interview delivery mode + default language.
      // `extraSaveFields` may already carry an explicit delivery mode/language
      // chosen in the drawer; only fall back to defaults when it doesn't.
      else if (!isAiModStudy(oldKor) && isAiModStudy(newKor)) {
        const nextDeliveryMode =
          extraSaveFields?.interviewDeliveryMode ?? 'video';
        const nextStudyLanguage =
          extraSaveFields?.studyLanguage ?? DEFAULT_STUDY_LANGUAGE;
        dispatch({
          type: 'SET_INTERVIEW_DELIVERY_MODE',
          interviewDeliveryMode: nextDeliveryMode,
          historyOrigin,
        });
        dispatch({
          type: 'SET_STUDY_LANGUAGE',
          studyLanguage: nextStudyLanguage,
          historyOrigin,
        });
        void flushSave({
          ...extraSaveFields,
          ...(shouldClearSurveySettings
            ? {
                task: null,
                exposeParticipantEmails: false,
              }
            : {}),
          interviewDeliveryMode: nextDeliveryMode,
          studyLanguage: nextStudyLanguage,
        });
      }
      // Survey-like → non-survey-like: clear task/survey settings
      else if (shouldClearSurveySettings) {
        void flushSave({
          ...extraSaveFields,
          task: null,
          exposeParticipantEmails: false,
        });
      }
      // All other transitions
      else if (extraSaveFields) {
        void flushSave(extraSaveFields);
      }
    },
    [flushSave],
  );

  // Helper to compute participant capping and save fields when project type changes
  const computeParticipantCapping = useCallback(
    (newKor: KindOfResearch, newRct: ResearchConductType) => {
      const increased = hasIncreasedTargetLimitEnabled === true;
      const max = getParticipantTargetLimit(newKor, increased);
      const prevCount = state.targeting.numberOfParticipants;
      const cappedCount = Math.min(prevCount, max);
      // A study recruiting only the researcher's own participants gets the
      // panel back when it moves to a type the link cannot admit to.
      const restoredPanelCount = panelCountAfterKindChange({
        recruitFromPanel: state.targeting.recruitFromPanel !== false,
        newKindAdmitsExternal: supportsExternalParticipants(newKor),
        keptCount: prevCount,
        externalTarget: project?.recruitmentTargets?.externalParticipants,
        fallback: DEFAULT_EXTERNAL_PARTICIPANTS_TARGET,
        limit: max,
      });
      const saveExtra: DraftSaveFields = {
        kindOfResearch: newKor,
        researchConductType: newRct,
        ...(prevCount > max && { numberOfParticipants: cappedCount }),
        ...(restoredPanelCount !== undefined && {
          numberOfParticipants: restoredPanelCount,
        }),
      };
      return { max, prevCount, cappedCount, restoredPanelCount, saveExtra };
    },
    [
      state.targeting.numberOfParticipants,
      state.targeting.recruitFromPanel,
      project?.recruitmentTargets?.externalParticipants,
      hasIncreasedTargetLimitEnabled,
    ],
  );

  const handleProjectTypeUpdate = useCallback(
    (data: { projectType: string }) => {
      const { kindOfResearch: newKor, researchConductType: newRct } =
        resolveStudyType(data.projectType);

      const { cappedCount, prevCount, restoredPanelCount, saveExtra } =
        computeParticipantCapping(newKor, newRct);

      const actions = [
        ...buildProjectTypeHistory(
          state.kindOfResearch,
          newKor,
          newRct,
          cappedCount,
          prevCount,
        ),
        ...restorePanelActions(restoredPanelCount),
      ];

      const spotlight = {
        section: 'projectType',
        field: 'projectType',
      } as const;

      dispatch({
        type: 'HISTORY_BATCH',
        label: 'AI: Update project type',
        actions,
        spotlight,
        origin: 'ai',
      });
      requestSpotlight(spotlight);
      applyProjectTypeTransition(state.kindOfResearch, newKor, saveExtra, 'ai');
    },
    [
      state.kindOfResearch,
      applyProjectTypeTransition,
      buildProjectTypeHistory,
      computeParticipantCapping,
      requestSpotlight,
    ],
  );

  const handleScreenerPatch = useCallback(
    (patch: AiScreenerPatch) => {
      const current = preStreamState().screenerQuestions;
      const { questions, spotlightUid } = applyScreenerPatch(
        current,
        patch,
        screenerMaxQuestions,
      );
      // The organization's minimum (2, or 0 on the allow-zero flag, PX-453)
      // is enforced at launch, so a change that would take a screener from
      // meeting it to breaching it is refused here rather than failing later.
      if (
        questions.length < screenerMinQuestions &&
        current.length >= screenerMinQuestions
      ) {
        console.warn(
          `[ai-chat] refused a screener change that would leave fewer than ${screenerMinQuestions} questions`,
        );
        return;
      }

      if (patch.replace) {
        // AI delivered a full screener set. has_honeypot is not modelled in
        // the screener form yet, and generation_time_ms is deferred.
        analytics?.track({
          action: 'generated',
          actionSubject: 'screenerQuestions',
          attributes: { projectId, questions_count: questions.length },
        });
      }

      applyAiAction(
        {
          type: 'SET_SCREENER',
          payload: questions,
          spotlight: {
            section: 'screener',
            ...(spotlightUid && { screenerQuestionUid: spotlightUid }),
          },
        },
        buildScreenerPayload(questions),
      );
    },
    [
      analytics,
      projectId,
      applyAiAction,
      screenerMaxQuestions,
      screenerMinQuestions,
      preStreamState,
    ],
  );

  const handleWelcomeScreenUpdate = useCallback(
    (patch: Partial<WelcomeScreenData>) => {
      const base = preStreamState().welcomeScreen;
      const data: WelcomeScreenData = {
        title: patch.title ?? base.title,
        message: patch.message ?? base.message,
      };
      // Pick the field to spotlight by diffing against the draft, not by
      // truthiness — an AI update touching only `message` on a screen that
      // already has a title should highlight `message`, not `title`.
      const titleChanged = data.title !== base.title;
      const messageChanged = data.message !== base.message;
      if (!titleChanged && !messageChanged) return;
      const spotlight: AiProjectSpotlightTarget =
        titleChanged === messageChanged
          ? { section: 'welcomeScreen' }
          : {
              section: 'welcomeScreen',
              field: titleChanged ? 'title' : 'message',
            };

      applyAiAction(
        { type: 'SET_WELCOME_SCREEN', payload: data, spotlight },
        buildWelcomeScreenPayload(data),
      );
    },
    [applyAiAction, preStreamState],
  );

  const handleClosingMessageUpdate = useCallback(
    (patch: Partial<ClosingMessageData>) => {
      const base = preStreamState().closingMessage;
      const data: ClosingMessageData = {
        closingMessage: patch.closingMessage ?? base.closingMessage,
      };
      if (data.closingMessage === base.closingMessage) return;
      applyAiAction(
        {
          type: 'SET_CLOSING_MESSAGE',
          payload: data,
          spotlight: { section: 'closingMessage' },
        },
        buildClosingMessagePayload(data),
      );
    },
    [applyAiAction, preStreamState],
  );

  const handleInterviewPatch = useCallback(
    (patch: AiInterviewPatch) => {
      const base = preStreamState().interviewSections;
      const { sections, spotlight } = applyInterviewPatch(base, patch);
      if (sections === base) return;
      applyAiAction(
        { type: 'SET_INTERVIEW_SECTIONS', payload: sections, spotlight },
        buildInterviewSectionsPayload(sections),
      );
    },
    [applyAiAction, preStreamState],
  );

  const handleStepComplete = useCallback((step: AiProjectStep) => {
    dispatch({ type: 'COMPLETE_STEP', step });
  }, []);

  // ── Undo / Redo ──
  const handleUndo = useCallback(() => {
    const entry = state._history.past.at(-1);
    if (!entry) return;
    analytics?.ui({
      action: 'action',
      actionSubject: 'editorHistory',
      source: 'projectEditor',
      attributes: { action: 'undo' },
    });
    applyHistoryEntry('undo', entry);
  }, [analytics, applyHistoryEntry, state._history.past]);

  const handleRedo = useCallback(() => {
    const entry = state._history.future[0];
    if (!entry) return;
    analytics?.ui({
      action: 'action',
      actionSubject: 'editorHistory',
      source: 'projectEditor',
      attributes: { action: 'redo' },
    });
    applyHistoryEntry('redo', entry);
  }, [analytics, applyHistoryEntry, state._history.future]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      const isEditable =
        target instanceof HTMLInputElement ||
        target instanceof HTMLTextAreaElement ||
        target instanceof HTMLSelectElement ||
        target?.isContentEditable === true;

      const action = getHistoryShortcutAction({
        key: e.key,
        metaKey: e.metaKey,
        ctrlKey: e.ctrlKey,
        shiftKey: e.shiftKey,
        altKey: e.altKey,
        isComposing: e.isComposing,
        isEditableTarget: isEditable,
      });

      if (!action) return;
      e.preventDefault();
      if (action === 'undo') handleUndo();
      else handleRedo();
    };

    globalThis.addEventListener('keydown', handleKeyDown);
    return () => globalThis.removeEventListener('keydown', handleKeyDown);
  }, [handleUndo, handleRedo]);

  // ── Streaming callbacks (dispatch only, no save) ──
  // Wrapped with HISTORY_STREAMING so state updates eagerly for live preview
  // but history records one entry when the final tool-call handler fires.
  const stream = useCallback(
    (inner: AiProjectAction) =>
      dispatch({
        type: 'HISTORY_STREAMING',
        inner,
        origin: 'ai',
      } as WrappedAction),
    [],
  );

  const streamingCallbacks = useMemo(
    () => ({
      onOutlinePatch: (patch: AiOutlinePatch) => {
        for (const action of outlineChanges(patch, preStreamState().outline)
          .actions) {
          stream(action);
        }
      },
      onTargetingPatch: (patch: AiTargetingPatch) => {
        const max = getParticipantTargetLimit(
          stateRef.current.kindOfResearch,
          hasIncreasedTargetLimitEnabled === true,
        );
        for (const action of targetingChanges(
          patch,
          preStreamState().targeting,
          max,
        ).actions) {
          stream(action);
        }
      },
      onScreenerUpdate: (questions: FormQuestion[]) => {
        stream({
          type: 'SET_SCREENER',
          payload: questions.slice(0, screenerMaxQuestions),
        });
      },
      onWelcomeScreenUpdate: (data: Partial<WelcomeScreenData>) => {
        // A partial edit streams only the field it changes; the other keeps
        // its value from the draft rather than being wiped by the preview.
        const base = preStreamState().welcomeScreen;
        stream({
          type: 'SET_WELCOME_SCREEN',
          payload: {
            title: data.title ?? base.title,
            message: data.message ?? base.message,
          },
        });
      },
      onClosingMessageUpdate: (data: Partial<ClosingMessageData>) => {
        stream({
          type: 'SET_CLOSING_MESSAGE',
          payload: {
            closingMessage:
              data.closingMessage ??
              preStreamState().closingMessage.closingMessage,
          },
        });
      },
      onStreamedCallFailed: () => {
        dispatch({ type: 'HISTORY_STREAMING_REVERT' } as WrappedAction);
      },
      onInterviewSectionsUpdate: (sections: InterviewSectionData[]) => {
        // The preview is reconciled against the draft too, so attached media
        // and stable uids show through every streamed frame, not only once
        // the call completes (which applies against the same base).
        stream({
          type: 'SET_INTERVIEW_SECTIONS',
          payload: reconcileInterviewSections(
            sections,
            preStreamState().interviewSections,
          ),
        });
      },
    }),
    [
      stream,
      screenerMaxQuestions,
      preStreamState,
      hasIncreasedTargetLimitEnabled,
    ],
  );

  // Both sources change through one history batch, so a switch is one undo
  // entry and the "a study always keeps a source" rule holds on undo too:
  // own participants off brings the panel back in the same entry.
  const applySourcesBatch = useCallback(
    (
      label: string,
      change: ParticipantSourcesChange,
      origin: 'user' | 'ai',
    ) => {
      const targeting = stateRef.current.targeting;
      const actions: AiProjectAction[] = [];
      const next = { ...targeting };
      if (change.externalInvites !== undefined) {
        const own = applyExternalInvitesChange(
          targeting.ownParticipants ??
            externalParticipantsFromProject(project ?? undefined),
          change.externalInvites,
        );
        actions.push({
          type: 'PATCH_TARGETING',
          field: 'ownParticipants',
          value: own,
        });
        next.ownParticipants = own;
      }
      if (change.recruitFromPanel !== undefined) {
        actions.push({
          type: 'PATCH_TARGETING',
          field: 'recruitFromPanel',
          value: change.recruitFromPanel,
        });
        next.recruitFromPanel = change.recruitFromPanel;
      }
      if (actions.length === 0) return;
      dispatch({
        type: 'HISTORY_BATCH',
        label,
        actions,
        spotlight: { section: 'targeting' },
        origin,
      });
      if (origin === 'ai') requestSpotlight({ section: 'targeting' });
      void flushSave(buildTargetingPayload(next));
    },
    [project, flushSave, requestSpotlight],
  );
  const handleExternalInvitesChange = useCallback(
    (externalInvites: UpdateDraftExternalInvitesDto | null) => {
      const panelOff = stateRef.current.targeting.recruitFromPanel === false;
      applySourcesBatch(
        externalInvites === null
          ? 'Turn off own participants'
          : 'Update own participants',
        {
          externalInvites,
          ...(externalInvites === null &&
            panelOff && { recruitFromPanel: true }),
        },
        'user',
      );
    },
    [applySourcesBatch],
  );
  // The panel as a source lives in the reducer's targeting (it decides
  // whether the target audience exists at all) and saves as the panel
  // count: zero for nobody, the kept count when the panel comes back.
  const handleRecruitFromPanelChange = useCallback(
    (nextRecruitFromPanel: boolean) => {
      applySourcesBatch(
        nextRecruitFromPanel ? 'Recruit from the panel' : 'Panel off',
        { recruitFromPanel: nextRecruitFromPanel },
        'user',
      );
    },
    [applySourcesBatch],
  );
  // An AI call is resolved under the section's rules and applied as one
  // batch like any other AI edit. Ignored while the section is not on the
  // canvas: the tool is not offered then, so a call is a stale or forged one.
  const handleParticipantSourcesPatch = useCallback(
    (patch: AiParticipantSourcesPatch) => {
      if (!showParticipantSources) return;
      const targeting = stateRef.current.targeting;
      const change = resolveParticipantSourcesPatch(patch, {
        recruitFromPanel: targeting.recruitFromPanel !== false,
        external:
          targeting.ownParticipants ??
          externalParticipantsFromProject(project ?? undefined),
      });
      applySourcesBatch('AI: Update participant sources', change, 'ai');
    },
    [showParticipantSources, project, applySourcesBatch],
  );

  useAiToolCallHandler(messages, {
    onOutlinePatch: handleOutlinePatch,
    onProjectTypeUpdate: handleProjectTypeUpdate,
    onTargetingPatch: handleTargetingPatch,
    onParticipantSourcesPatch: handleParticipantSourcesPatch,
    onScreenerPatch: handleScreenerPatch,
    onWelcomeScreenUpdate: handleWelcomeScreenUpdate,
    onClosingMessageUpdate: handleClosingMessageUpdate,
    onInterviewPatch: handleInterviewPatch,
    onStepComplete: handleStepComplete,
    initialProcessedIds: restoredToolIds,
    stoppedMessageIds,
    streaming: streamingCallbacks,
  });

  // ── 5. User edits in editor pane ──
  const handleOutlineChange = useCallback(
    (field: keyof OutlineData, value: string) => {
      dispatch({
        type: 'PATCH_OUTLINE',
        field,
        value,
        coalescingKey: `outline.${field}`,
      });
      recordEdit('outline', field);
      scheduleSave({ [field]: value });
    },
    [recordEdit, scheduleSave],
  );

  // Top-right "Preview" toggle. Fire only when opening the participant-facing
  // preview, tagged with the step the researcher left from.
  const handleTogglePreview = useCallback(() => {
    if (isPreviewCollapsed) {
      analytics?.ui({
        action: 'clicked',
        actionSubject: 'preview',
        source: 'projectEditor',
        attributes: { from_step: state.currentStep },
      });
    }
    togglePreview();
  }, [analytics, isPreviewCollapsed, state.currentStep, togglePreview]);

  const jumpPreviewTo = useCallback(
    (step: PreviewJump['step'], index?: number) => {
      previewJumpNonceRef.current += 1;
      setPreviewJump({ step, index, nonce: previewJumpNonceRef.current });
      togglePreview(false);
    },
    [togglePreview],
  );

  const handlePreviewQuestion = useCallback(
    (index: number) => jumpPreviewTo('screener', index),
    [jumpPreviewTo],
  );

  const handlePreviewInterviewQuestion = useCallback(
    (index: number) => jumpPreviewTo('interview', index),
    [jumpPreviewTo],
  );

  const handlePreviewWelcome = useCallback(
    () => jumpPreviewTo('welcome'),
    [jumpPreviewTo],
  );

  const handlePreviewClosing = useCallback(
    () => jumpPreviewTo('complete'),
    [jumpPreviewTo],
  );

  const handleScreenerChange = useCallback(
    (questions: FormQuestion[]) => {
      const prev = screenerQuestionsRef.current;
      // A question present in `prev` but absent from `questions` was just deleted.
      // Store its UID as the spotlight so that on undo (when the question is back
      // in the DOM) `getSpotlightAnchorIds` finds the card directly; on redo
      // (question gone again) it falls back to the section header automatically.
      const deletedQuestion = prev.find(
        (p) => !questions.some((q) => q.uid === p.uid),
      );
      dispatch({
        type: 'PATCH_SCREENER',
        questions,
        spotlight: deletedQuestion
          ? { section: 'screener', screenerQuestionUid: deletedQuestion.uid }
          : undefined,
      });
      // Autosave only when the draft backend will accept it. The backend now
      // accepts incomplete screeners; only non-empty text is required. Launch
      // is gated separately on full validity.
      if (isScreenerDraftSavable(questions)) {
        scheduleSave(buildScreenerPayload(questions));
      }
      recordEdit('screener', 'questions');
    },
    [recordEdit, scheduleSave],
  );

  const handleWelcomeScreenChange = useCallback(
    (data: WelcomeScreenData) => {
      dispatch({ type: 'SET_WELCOME_SCREEN', payload: data });
      recordEdit('welcome', 'screen');
      // Held while a field is over its cap: the DTO would reject the whole
      // request. The editor says so inline and launch is blocked on it.
      if (isWelcomeScreenSavable(data)) {
        scheduleSave(buildWelcomeScreenPayload(data));
      }
    },
    [recordEdit, scheduleSave],
  );

  const handleClosingMessageChange = useCallback(
    (data: ClosingMessageData) => {
      dispatch({ type: 'SET_CLOSING_MESSAGE', payload: data });
      recordEdit('closing', 'message');
      if (isClosingMessageSavable(data)) {
        scheduleSave(buildClosingMessagePayload(data));
      }
    },
    [recordEdit, scheduleSave],
  );

  const handleInterviewSectionsChange = useCallback(
    (sections: InterviewSectionData[]) => {
      dispatch({ type: 'SET_INTERVIEW_SECTIONS', payload: sections });
      recordEdit('interview', 'sections');
      if (isInterviewSectionsSavable(sections)) {
        scheduleSave(buildInterviewSectionsPayload(sections));
      }
    },
    [recordEdit, scheduleSave],
  );

  const handleBeforeGenerateMediaContext = useCallback(async () => {
    const saved = await flushSave(
      buildInterviewSectionsPayload(state.interviewSections),
    );
    if (!saved) {
      throw new Error(t`Save the question before generating context.`);
    }
  }, [flushSave, state.interviewSections]);

  // ── 6. Step navigation ──
  const handleLaunch = useCallback(async () => {
    if (!state.projectId) return;
    analytics?.track({
      action: 'completed',
      actionSubject: 'projectEditor',
      attributes: { projectId: state.projectId, flow: initialFlow },
    });
    // Moving on before the draft has landed would publish whatever the server
    // last accepted, which is not what is on screen. The failure notification
    // is raised by the save-error effect above.
    const saved = await flushSave(buildFullPayload(state));
    if (!saved) return;

    navigate({
      to: '/researchers/projects/$projectId/launch',
      params: { projectId: state.projectId },
    });
  }, [state, flushSave, navigate, analytics, initialFlow]);

  // ── 6b. Quotas ──
  // A quota rule may reference a screener answer typed seconds ago, which the
  // server only knows once the autosave lands; the quota save waits for it.
  const handleQuotaPrepareSave = useCallback(async () => {
    const saved = await flushSave(buildFullPayload(stateRef.current));
    return saved ? stateRef.current.projectId : null;
  }, [flushSave]);
  // Memoized so the canvas's memo still skips stream-driven re-renders.
  const quotaSection = useMemo(
    () => (
      <QuotaCanvasSection
        project={project ?? undefined}
        organizationId={effectiveOrgId ?? undefined}
        screenerQuestions={state.screenerQuestions}
        prepareSave={handleQuotaPrepareSave}
        spotlightId="section:quotas"
        hiddenForExternalParticipants={externalParticipantsOn}
      />
    ),
    [
      project,
      effectiveOrgId,
      state.screenerQuestions,
      handleQuotaPrepareSave,
      externalParticipantsOn,
    ],
  );

  // ── 6a. Project type card selection ──
  const selectedStudyTypeId = useMemo(
    () => toStudyTypeId(state.kindOfResearch, state.researchConductType),
    [state.kindOfResearch, state.researchConductType],
  );

  const handleProjectTypeSelect = useCallback(
    (studyTypeId: string) => {
      const { kindOfResearch: newKor, researchConductType: newRct } =
        resolveStudyType(studyTypeId);

      const { cappedCount, prevCount, restoredPanelCount, saveExtra } =
        computeParticipantCapping(newKor, newRct);

      const actions: AiProjectAction[] = [
        { type: 'SET_KIND_OF_RESEARCH', kindOfResearch: newKor },
        { type: 'SET_RESEARCH_CONDUCT_TYPE', researchConductType: newRct },
      ];
      if (isAiModStudy(state.kindOfResearch) && !isAiModStudy(newKor)) {
        actions.push({ type: 'CLEAR_AI_MOD_FIELDS' });
      } else if (!isAiModStudy(state.kindOfResearch) && isAiModStudy(newKor)) {
        actions.push({
          type: 'SET_INTERVIEW_DELIVERY_MODE',
          interviewDeliveryMode: 'video',
        });
        actions.push({
          type: 'SET_STUDY_LANGUAGE',
          studyLanguage: DEFAULT_STUDY_LANGUAGE,
        });
      }
      if (prevCount > cappedCount) {
        actions.push({
          type: 'PATCH_TARGETING',
          field: 'numberOfParticipants',
          value: cappedCount,
        });
      }
      actions.push(...restorePanelActions(restoredPanelCount));

      dispatch({
        type: 'HISTORY_BATCH',
        label: 'Select project type',
        actions,
        spotlight: { section: 'projectType', field: 'projectType' },
      });
      applyProjectTypeTransition(state.kindOfResearch, newKor, saveExtra);

      // Insert a marker message into the chat history (no AI call).
      // The chat pane renders [ProjectType:...] as a confirmation card.
      const studyTypeLabel =
        ALL_STUDY_TYPES.find((s) => s.id === studyTypeId)?.label() ??
        studyTypeId;
      setMessages((prev) => [
        ...prev,
        {
          id: generateId(),
          role: 'user' as const,
          parts: [
            { type: 'text' as const, text: `[ProjectType:${studyTypeId}]` },
          ],
          createdAt: new Date(),
        },
        {
          id: generateId(),
          role: 'assistant' as const,
          parts: [
            {
              type: 'text' as const,
              text: t`You've selected **${studyTypeLabel}** as your project type. You can change this anytime from the project canvas.`,
            },
          ],
          createdAt: new Date(),
        },
      ]);

      // Advance to projectType step so suggestion chips show "Set up targeting"
      dispatch({ type: 'SET_STEP', step: 'projectType' });
    },
    [
      applyProjectTypeTransition,
      setMessages,
      state.kindOfResearch,
      computeParticipantCapping,
    ],
  );

  // Language change drops a [StudyLanguage:old>new] marker into the chat
  // (rendered as a confirmation card with a rewrite CTA — no AI call), same
  // pattern as the [ProjectType:...] marker.
  const offerLanguageRegen = useCallback(
    (oldLanguage: string, newLanguage: string) => {
      setMessages((prev) => [
        ...prev,
        {
          id: generateId(),
          role: 'user' as const,
          parts: [
            {
              type: 'text' as const,
              text: buildStudyLanguageMarker(oldLanguage, newLanguage),
            },
          ],
          createdAt: new Date(),
        },
      ]);
      if (isChatCollapsed) toggleChat(false);
    },
    [setMessages, isChatCollapsed, toggleChat],
  );

  // ── 6b. Study-type drawer save ──
  // Single handler for the project-type drawer (drafts and existing projects
  // alike). The reducer is the source of truth; this never re-reads the query
  // cache. A *type change* routes through the same atomic flow as the chat
  // picker (one HISTORY_BATCH + an immediate flushSave via
  // applyProjectTypeTransition). A same-type *tweak* (duration / delivery mode /
  // language) only updates the reducer and debounces the save so slider and
  // select churn coalesces.
  const handleStudyTypeSave = useCallback(
    (payload: StudyTypeSavePayload) => {
      const newKor = payload.kindOfResearch;
      const newRct = payload.researchConductType;
      const isTypeChange =
        newKor !== state.kindOfResearch || newRct !== state.researchConductType;
      const isAiInterview = isAiModStudy(newKor);

      // Language changed on an AI-mod study → offer regen (null ≡ 'en').
      const prevLanguage = state.studyLanguage ?? DEFAULT_STUDY_LANGUAGE;
      const nextLanguage = payload.studyLanguage ?? DEFAULT_STUDY_LANGUAGE;
      const shouldOfferLanguageRegen =
        isAiInterview && nextLanguage !== prevLanguage;

      if (!isTypeChange) {
        // Same project type — only fine-grained settings changed. AI-interview
        // payloads carry no duration; it rides the sections autosave.
        if (payload.duration != null) {
          dispatch({ type: 'SET_DURATION', duration: payload.duration });
        }
        if (payload.locationRespondentInPerson) {
          dispatch({
            type: 'PATCH_TARGETING',
            field: 'locationInPerson',
            value: payload.locationRespondentInPerson,
          });
        }
        if (payload.interviewDeliveryMode) {
          dispatch({
            type: 'SET_INTERVIEW_DELIVERY_MODE',
            interviewDeliveryMode: payload.interviewDeliveryMode,
          });
        }
        if (payload.studyLanguage != null) {
          dispatch({
            type: 'SET_STUDY_LANGUAGE',
            studyLanguage: payload.studyLanguage,
          });
        }
        if (payload.responseLanguages != null) {
          dispatch({
            type: 'SET_RESPONSE_LANGUAGES',
            responseLanguages: payload.responseLanguages,
          });
        }
        // Switching a study to text takes its screen recording with it. The
        // reducer already dropped the flag from local state; the script has
        // to ride this save too, or the server keeps `recordScreen: true`
        // stored with no toggle left on screen to clear it.
        const sectionsWithoutScreenRecording =
          payload.interviewDeliveryMode === 'text'
            ? clearRecordScreen(state.interviewSections)
            : state.interviewSections;
        const clearedScreenRecording =
          sectionsWithoutScreenRecording !== state.interviewSections;

        scheduleSave({
          ...(payload.duration != null ? { duration: payload.duration } : {}),
          ...(payload.locationRespondentInPerson
            ? { locationRespondentInPerson: payload.locationRespondentInPerson }
            : {}),
          ...(payload.interviewDeliveryMode
            ? { interviewDeliveryMode: payload.interviewDeliveryMode }
            : {}),
          ...(clearedScreenRecording
            ? buildInterviewSectionsPayload(sectionsWithoutScreenRecording)
            : {}),
          ...(payload.studyLanguage != null
            ? { studyLanguage: payload.studyLanguage }
            : {}),
          // Empty selection is an explicit clear (`null` unsets server-side).
          ...(payload.responseLanguages != null
            ? {
                responseLanguages:
                  payload.responseLanguages.length > 0
                    ? payload.responseLanguages
                    : null,
              }
            : {}),
        });
        if (shouldOfferLanguageRegen) {
          offerLanguageRegen(prevLanguage, nextLanguage);
        }
        return;
      }

      // Project type changed — atomic batch + immediate save, mirroring the
      // chat picker so both entry points stay in sync.
      const { cappedCount, prevCount, restoredPanelCount, saveExtra } =
        computeParticipantCapping(newKor, newRct);

      // AI interviews derive duration from the script; other types take the
      // slider value. `null` (empty script) keeps the stored value.
      const nextDuration = isAiInterview
        ? estimateInterviewDurationMinutes(state.interviewSections)
        : (payload.duration ?? null);

      const actions: AiProjectAction[] = [
        { type: 'SET_KIND_OF_RESEARCH', kindOfResearch: newKor },
        { type: 'SET_RESEARCH_CONDUCT_TYPE', researchConductType: newRct },
      ];
      if (nextDuration != null) {
        actions.push({ type: 'SET_DURATION', duration: nextDuration });
      }
      if (payload.locationRespondentInPerson) {
        actions.push({
          type: 'PATCH_TARGETING',
          field: 'locationInPerson',
          value: payload.locationRespondentInPerson,
        });
      }
      if (!isAiModStudy(state.kindOfResearch) && isAiInterview) {
        actions.push({
          type: 'SET_INTERVIEW_DELIVERY_MODE',
          interviewDeliveryMode: payload.interviewDeliveryMode ?? 'video',
        });
        actions.push({
          type: 'SET_STUDY_LANGUAGE',
          studyLanguage: payload.studyLanguage ?? DEFAULT_STUDY_LANGUAGE,
        });
        actions.push({
          type: 'SET_RESPONSE_LANGUAGES',
          responseLanguages: payload.responseLanguages ?? [],
        });
      }
      if (prevCount > cappedCount) {
        actions.push({
          type: 'PATCH_TARGETING',
          field: 'numberOfParticipants',
          value: cappedCount,
        });
      }
      actions.push(...restorePanelActions(restoredPanelCount));

      dispatch({
        type: 'HISTORY_BATCH',
        label: 'Select project type',
        actions,
        spotlight: { section: 'projectType', field: 'projectType' },
      });

      // applyProjectTypeTransition clears/repopulates AI-mod fields and fires
      // the immediate save. Forward the drawer's explicit delivery mode and
      // language so they win over the defaults when entering AI mod.
      applyProjectTypeTransition(state.kindOfResearch, newKor, {
        ...saveExtra,
        ...(nextDuration != null ? { duration: nextDuration } : {}),
        ...(payload.locationRespondentInPerson
          ? { locationRespondentInPerson: payload.locationRespondentInPerson }
          : {}),
        ...(isAiInterview && payload.interviewDeliveryMode
          ? { interviewDeliveryMode: payload.interviewDeliveryMode }
          : {}),
        ...(isAiInterview && payload.studyLanguage != null
          ? { studyLanguage: payload.studyLanguage }
          : {}),
        ...(isAiInterview && payload.responseLanguages?.length
          ? { responseLanguages: payload.responseLanguages }
          : {}),
      });

      // Surface the next-step CTA, consistent with the chat picker.
      dispatch({ type: 'SET_STEP', step: 'projectType' });

      if (shouldOfferLanguageRegen) {
        offerLanguageRegen(prevLanguage, nextLanguage);
      }
    },
    [
      state.kindOfResearch,
      state.researchConductType,
      state.studyLanguage,
      state.interviewSections,
      computeParticipantCapping,
      applyProjectTypeTransition,
      scheduleSave,
      offerLanguageRegen,
    ],
  );

  // ── 7. Derived values ──
  // Live inferred duration for AI interviews; `null` while the script has no
  // questions.
  const inferredAiDuration = useMemo(
    () => estimateInterviewDurationMinutes(state.interviewSections),
    [state.interviewSections],
  );

  const screenerValidationErrors = useMemo(
    () =>
      getScreenerValidationIssues(
        state.screenerQuestions,
        screenerMaxQuestions,
        screenerMinQuestions,
      ),
    [state.screenerQuestions, screenerMaxQuestions, screenerMinQuestions],
  );

  const isScreenerSavable = isScreenerDraftSavable(state.screenerQuestions);
  const showsSavedScreener = isShowingSavedScreener({
    saveStatus: state.saveStatus,
    hasPendingSave: hasPending(),
    isSaveInFlight: isBusy(),
    isScreenerSavable,
  });
  const canRecheckStudyChecks =
    studyChecksEnabled &&
    Boolean(state.projectId) &&
    !isLoading &&
    !screenerConflict &&
    showsSavedScreener;
  // Autosave skips a screener the draft backend would reject, so the header
  // still reads "Saved": say what's actually needed.
  const unsavableScreenerMessage = isScreenerSavable
    ? undefined
    : t`Fill in any blank questions and choices so the screener can save, then re-check.`;

  const studyChecksQuery = useStudyChecks({
    projectId: state.projectId,
    questions: state.screenerQuestions,
    study: {
      description: state.outline.description,
      studyGoals: state.outline.studyGoals,
      audienceType: state.targeting.talkToAudience,
      researchConductType: state.researchConductType,
    },
    // Check the saved screener; the hook waits for edits to settle first.
    enabled:
      studyChecksEnabled &&
      state.screenerQuestions.length > 0 &&
      !isLoading &&
      showsSavedScreener,
    canRecheck: canRecheckStudyChecks,
  });

  // Concept-testing errors the server would reject too (a concept block on a
  // statement-only section, an over-the-ceiling expansion, a concept with
  // content but no internal name). Concept WARNINGS stay inline in the block:
  // they are advisory, and this list disables the launch CTA.
  const interviewValidationErrors = useMemo(
    () =>
      getInterviewLaunchErrors(state.interviewSections, {
        usabilityTesting: usabilityTestingOffer(
          usabilityTestingEnabled,
          state.interviewDeliveryMode,
        ),
      }),
    [
      state.interviewSections,
      state.interviewDeliveryMode,
      usabilityTestingEnabled,
    ],
  );

  const fieldLengthErrors = useMemo(
    () =>
      isAiModStudy(state.kindOfResearch)
        ? getFieldLengthLaunchErrors({
            welcomeScreen: state.welcomeScreen,
            closingMessage: state.closingMessage,
            interviewSections: state.interviewSections,
          })
        : [],
    [
      state.kindOfResearch,
      state.welcomeScreen,
      state.closingMessage,
      state.interviewSections,
    ],
  );

  // Launch is gated on the Progress checklist: every step shown for this
  // project type must be complete. Each incomplete step surfaces as a warning
  // so the disabled CTA is self-explanatory.
  const { dismiss: dismissCheck, isDismissed: isDismissedCheck } =
    useDismissedStudyChecks(state.projectId);
  const {
    result: studyCheckResult,
    isError: isStudyCheckError,
    isFindingCurrent,
  } = studyChecksQuery;
  // What blocks launch. With study checks on these lead the checks drawer;
  // with them off they are the header's "Fix issues before launching" list.
  const launchBlockers = useMemo(() => {
    // Until the project is loaded the reducer holds the empty initial state,
    // which fails every step and screener rule: checks computed from it are
    // noise (they briefly showed "5 issues" on every reload).
    if (!isHydrated) return [];
    const blockers: StudyCheck[] = [];
    for (const step of stepOrder) {
      if (stepCompletion.status[step] === 'incomplete') {
        blockers.push({
          id: `step-${step}`,
          message: CHECKLIST_LABELS[step](),
          severity: 'blocker',
          section: step,
        });
      }
    }
    for (const [index, issue] of screenerValidationErrors.entries()) {
      const { message, questionNumber, questionUid, rule } = issue;
      const question = questionNumber
        ? state.screenerQuestions[Number(questionNumber) - 1]
        : undefined;
      blockers.push({
        id: `screener-${index}`,
        ...(rule === 'no-qualifying-answer' && {
          title: t`Every option disqualifies, so no one can pass this question`,
        }),
        message,
        severity: 'blocker',
        questionUid,
        validationRule: rule,
        ...(questionNumber &&
          question && {
            question: { text: question.text },
          }),
        section: questionNumber
          ? `screenerQuestion ${questionNumber}`
          : 'screener',
        ...(questionNumber && {
          aiPrompt: `The question fails a launch check: ${message}. Any fix should preserve the researcher's intended qualification criteria; if the intent is unclear, explain the trade-off rather than guessing who should qualify.`,
        }),
      });
    }
    for (const [index, message] of interviewValidationErrors.entries()) {
      blockers.push({ id: `interview-${index}`, message, severity: 'blocker' });
    }
    for (const [index, message] of fieldLengthErrors.entries()) {
      blockers.push({
        id: `field-length-${index}`,
        message,
        severity: 'blocker',
      });
    }
    return blockers;
  }, [
    isHydrated,
    stepOrder,
    stepCompletion,
    screenerValidationErrors,
    interviewValidationErrors,
    fieldLengthErrors,
    state.screenerQuestions,
  ]);
  const studyChecks = useMemo(() => {
    if (!studyChecksEnabled) return launchBlockers;
    return buildStudyChecks({
      blockers: launchBlockers,
      questions: state.screenerQuestions,
      result: studyCheckResult,
      isError: isStudyCheckError,
      isCurrent: isFindingCurrent,
      isDismissed: isDismissedCheck,
    });
  }, [
    studyChecksEnabled,
    launchBlockers,
    state.screenerQuestions,
    studyCheckResult,
    isStudyCheckError,
    isFindingCurrent,
    isDismissedCheck,
  ]);

  /**
   * Serialize the current value of a section so the AI has context. Reads
   * `stateRef` (it only runs on click) so its identity stays stable: every
   * AI-edit/flag-fix callback derives from it, and those are passed to every
   * question row — depending on `state` re-rendered all rows per keystroke.
   */
  const getSectionSnapshot = useCallback((section: string): string => {
    const state = stateRef.current;
    switch (section) {
      case 'internalTitle':
        return `Current internal title: "${state.outline.internalName}"`;
      case 'studyTitle':
        return `Current public title: "${state.outline.name}"\nCurrent internal name: "${state.outline.internalName}"`;
      case 'studyDescription':
        return `Current description: "${state.outline.description}"`;
      case 'projectGoals':
        return `Current project goals: "${state.outline.studyGoals}"`;
      case 'externalDetails':
        return `Current external title: "${state.outline.name}"\nCurrent description: "${state.outline.description}"`;
      case 'targeting': {
        const t = state.targeting;
        const audience =
          t.talkToAudience === 1
            ? 'professionals (B2B)'
            : t.talkToAudience === 2
              ? 'general population (B2C)'
              : 'not set';
        const lines = [
          `Audience type: ${audience}`,
          `Number of participants: ${t.numberOfParticipants || 'not set'}`,
          `Countries: ${t.countries.length > 0 ? t.countries.join(', ') : 'worldwide / not set'}`,
          describeTargetingAge(t) ?? 'Age range: All ages (18+)',
          `Genders: ${t.genders || 'not set'}`,
          `Job functions: ${t.jobFunctions.length > 0 ? t.jobFunctions.join(', ') : 'not set'}`,
          `Job titles: ${t.jobTitles.length > 0 ? t.jobTitles.map((j) => j.name).join(', ') : 'not set'}`,
          `Topics: ${t.topics.length > 0 ? t.topics.map((x) => x.name).join(', ') : 'not set'}`,
        ];
        return `Current target audience:\n${lines.join('\n')}`;
      }
      case 'screenerQuestions':
        return `Current screener questions and qualification settings:\n${JSON.stringify(state.screenerQuestions)}`;
      case 'welcomeMessage':
        return `Current welcome title: "${state.welcomeScreen.title}"\nCurrent welcome message: "${state.welcomeScreen.message}"`;
      case 'interviewQuestions':
        return `Current interview sections:\n${state.interviewSections.map((s) => `Section "${s.name}": ${s.questions.map((q) => `"${q.text}"`).join(', ')}`).join('\n')}`;
      case 'closingMessage':
        return `Current closing message: "${state.closingMessage.closingMessage}"`;
      default: {
        // Per-question: "screenerQuestion 2" → index 1
        const sqMatch = section.match(/^screenerQuestion (\d+)$/);
        if (sqMatch) {
          const idx = Number(sqMatch[1]) - 1;
          const q = state.screenerQuestions[idx];
          if (q)
            return `Current Q${idx + 1} and its qualification settings: ${JSON.stringify(q)}\nAdjacent questions and skip destinations: ${JSON.stringify(state.screenerQuestions.map((question, index) => ({ number: index + 1, uid: question.uid, text: question.text })))}`;
        }
        // Per interview question: "interviewQuestion 1 in section "Xyz""
        const iqMatch = section.match(
          /^interviewQuestion (\d+) in section "(.+)"$/,
        );
        if (iqMatch) {
          const qIdx = Number(iqMatch[1]) - 1;
          const secName = iqMatch[2];
          const sec = state.interviewSections.find((s) => s.name === secName);
          const q = sec?.questions[qIdx];
          if (q) return `Current question: "${q.text}"`;
        }
        return '';
      }
    }
  }, []);

  const handleAiEditAction = useCallback(
    (
      section: string,
      action: {
        type: string;
        tone?: string;
        prompt?: string;
        activityDetail?: string;
        toolHint?: string;
      },
    ) => {
      // Chat is single-flight. The toolbar bypasses the chat input's own guard,
      // so bail while a turn is already in flight rather than firing a second
      // concurrent send that races and can clobber the streaming draft edits.
      if (isLoading) return;

      const SECTION_LABELS: Record<string, string> = {
        internalTitle: 'internal title',
        studyTitle: 'public study title',
        studyDescription: 'public study description',
        projectGoals: 'project goals',
        externalDetails: 'external title and description',
        targeting: 'target audience',
        screenerQuestions: 'all screener questions',
        welcomeMessage: 'welcome message',
        interviewQuestions: 'all interview questions',
        closingMessage: 'closing message',
      };
      const label = SECTION_LABELS[section] ?? section;
      const snapshot = getSectionSnapshot(section);

      let instruction: string;
      if (action.type === 'rewrite') {
        instruction = `Rewrite the ${label}. Keep the same intent but improve clarity and wording.`;
      } else if (action.type === 'tone' && action.tone) {
        instruction = `Rewrite the ${label} in a ${action.tone.toLowerCase()} tone.`;
      } else if (
        (action.type === 'custom' || action.type === 'review') &&
        action.prompt
      ) {
        instruction = `For the ${label}: ${action.prompt}`;
      } else {
        return;
      }

      // Hint the AI to use the right tool for the surface being edited.
      const sqMatch = section.match(/^screenerQuestion (\d+)$/);
      const iqMatch = section.match(
        /^interviewQuestion (\d+) in section "(.+)"$/,
      );
      const surfaceToolHint = sqMatch
        ? ` Use updateScreener with one update entry for questionNumber ${sqMatch[1]}, changing only the fields that need it.`
        : iqMatch
          ? ` Use updateInterview with one updateQuestion entry for sectionName "${iqMatch[2]}" and questionNumber ${iqMatch[1]}, changing only the fields that need it.`
          : (TOOL_HINT_BY_SURFACE[section] ?? '');
      const toolHint = action.toolHint ?? surfaceToolHint;

      // Capture which AI-edit action ran on which surface. Reason keyword only
      // (the tone enum), never the custom prompt text, so the signal stays
      // content-free. Per-question surfaces normalise to a stable token.
      const surface = sqMatch
        ? 'screenerQuestion'
        : iqMatch
          ? 'interviewQuestion'
          : section;
      analytics?.track({
        action: action.type,
        actionSubject: 'aiCreateAiEdit',
        attributes: {
          ...(state.projectId && { projectId: state.projectId }),
          surface,
          ...(action.type === 'tone' && action.tone && { tone: action.tone }),
        },
      });

      // System instruction with current values, so the AI can act (or, for a
      // review, explain) without asking for them.
      const prompt = buildAiEditPrompt({
        mode: action.type === 'review' ? 'review' : 'edit',
        label,
        instruction,
        snapshot,
        toolHint,
        activityDetail: action.activityDetail,
      });

      if (isChatCollapsed) toggleChat(false);
      handleSend(prompt);
    },
    [
      analytics,
      state.projectId,
      isChatCollapsed,
      isLoading,
      toggleChat,
      handleSend,
      getSectionSnapshot,
    ],
  );

  const { handleOutlineFlagFix, handleQuestionFlagFix, isFlagFixing } =
    useQuestionFlagFix({
      studyTitle: state.outline.name,
      studyDescription: state.outline.description,
      handleSend,
      getSectionSnapshot,
      isChatCollapsed,
      toggleChat,
      chatStatus: status,
      analytics,
      projectId: state.projectId,
    });

  // ── 7b. Stable props for the memoized panes ──
  // Inline lambdas/objects would defeat ChatPane/Canvas/Preview's memo().
  const handleStepNavigate = useCallback((step: AiProjectStep) => {
    dispatch({ type: 'SET_STEP', step });
  }, []);

  const openTargetingDrawer = useCallback(
    (trigger: 'panel_section_click' | 'study_check') => {
      analytics?.ui({
        action: 'opened',
        actionSubject: 'targetingPanel',
        source: 'projectEditor',
        attributes: { trigger },
      });
      setIsTargetingDrawerOpen(true);
    },
    [analytics],
  );

  const handleTargetingClick = useCallback(
    () => openTargetingDrawer('panel_section_click'),
    [openTargetingDrawer],
  );

  // Memoized like the Quotas section so the canvas's memo still skips
  // stream-driven re-renders. Takes over the Target audience card's spotlight
  // id: the audience chips live in its panel card.
  const participantSourcesSection = useMemo(
    () =>
      showParticipantSources ? (
        <ParticipantSourcesSection
          project={project ?? undefined}
          targeting={state.targeting}
          onTargetingClick={handleTargetingClick}
          onAiEditAction={handleAiEditAction}
          recruitFromPanel={recruitFromPanel}
          onRecruitFromPanelChange={handleRecruitFromPanelChange}
          external={
            ownParticipants ??
            externalParticipantsFromProject(project ?? undefined)
          }
          onExternalInvitesChange={handleExternalInvitesChange}
          spotlightId="section:targeting"
        />
      ) : null,
    [
      showParticipantSources,
      project,
      state.targeting,
      handleTargetingClick,
      handleAiEditAction,
      recruitFromPanel,
      handleRecruitFromPanelChange,
      ownParticipants,
      handleExternalInvitesChange,
    ],
  );

  const handleProjectTypeClick = useCallback(() => {
    if (!isNewProject) void flushSave({});
    setIsStudyTypeDrawerOpen(true);
  }, [isNewProject, flushSave]);

  const handleQuestionFocus = useCallback(
    (screenerQuestionUid: string) => {
      requestSpotlight({ section: 'screener', screenerQuestionUid });
    },
    [requestSpotlight],
  );

  const handleToggleChatClick = useCallback(() => toggleChat(), [toggleChat]);

  const handleClosePreview = useCallback(
    () => togglePreview(true),
    [togglePreview],
  );

  const previousQuestionsConfig = useMemo(() => {
    const organizationId = project?.organizationId ?? dashboardOrgId;
    return !isNewProject &&
      project?.id &&
      teamId &&
      organizationId &&
      authUser?.id
      ? {
          currentProjectId: project.id,
          teamId,
          organizationId,
          authUserId: authUser.id,
        }
      : undefined;
  }, [
    isNewProject,
    project?.id,
    project?.organizationId,
    dashboardOrgId,
    teamId,
    authUser?.id,
  ]);

  const handleNavigateToCheck = useCallback(
    (check: StudyCheck) => {
      if (check.navigateTo?.section === 'targeting') {
        // Going to targeting from a targeting suggestion is taking it up.
        reportStudyCheckFeedback(state.projectId, check, 'acted-on');
        handleStepNavigate('targeting');
        openTargetingDrawer('study_check');
        return;
      }
      const target = resolveStudyCheckSpotlight(
        check,
        stepOrder,
        state.screenerQuestions,
      );
      if (target) {
        handleStepNavigate(target.section);
        requestSpotlight(target);
      }
    },
    [
      state.projectId,
      state.screenerQuestions,
      requestSpotlight,
      stepOrder,
      handleStepNavigate,
      openTargetingDrawer,
    ],
  );

  const handleFixCheckWithAi = useCallback(
    (check: StudyCheck, relatedChecks?: StudyCheck[]) => {
      if (!check.section || !check.aiPrompt) return;
      for (const finding of relatedChecks ?? [check]) {
        reportStudyCheckFeedback(state.projectId, finding, 'acted-on');
      }
      // Checks only suspect a problem: explain and offer a fix, don't apply it.
      const question = check.question;
      const concern = check.title ?? check.message.replace(/^Q\d+:\s*/, '');
      handleAiEditAction(check.section, {
        type: 'review',
        prompt: check.aiPrompt,
        toolHint: check.aiToolHint,
        activityDetail: question?.text.trim()
          ? `“${question.text.trim()}” · ${concern}`
          : concern,
      });
    },
    [handleAiEditAction, state.projectId],
  );

  const handleDismissCheck = useCallback(
    (check: StudyCheck) => {
      dismissCheck(check);
      reportStudyCheckFeedback(state.projectId, check, 'ignored');
    },
    [dismissCheck, state.projectId],
  );

  const handleReviewStudyWithAi = useCallback(() => {
    if (!canRecheckStudyChecks || isLoading) return;
    if (isChatCollapsed) toggleChat(false);
    handleSend(buildFullStudyReviewPrompt());
  }, [
    canRecheckStudyChecks,
    isLoading,
    isChatCollapsed,
    toggleChat,
    handleSend,
  ]);

  const questionChecks = useMemo(
    () => ({
      checks: studyChecks.filter((check) => check.questionUid),
      onFixWithAi: isLoading ? undefined : handleFixCheckWithAi,
      onDismiss: handleDismissCheck,
      onNavigateTo: handleNavigateToCheck,
    }),
    [
      studyChecks,
      isLoading,
      handleFixCheckWithAi,
      handleDismissCheck,
      handleNavigateToCheck,
    ],
  );

  // ── 8. Render ──
  return (
    <QuestionChecksContext.Provider
      value={studyChecksEnabled ? questionChecks : null}
    >
      <ProjectFlowLayout
        kor={state.kindOfResearch}
        currentStep="create"
        projectId={state.projectId ?? undefined}
        internalName={state.outline.internalName || undefined}
        saveStatus={
          screenerConflict
            ? 'conflict'
            : state.projectId
              ? (toLayoutSaveStatus(state.saveStatus) ??
                (!isLoading && !hasPending() && !isBusy()
                  ? 'saved'
                  : undefined))
              : 'draft'
        }
        studyChecks={
          !studyChecksEnabled ? undefined : !state.projectId ? [] : studyChecks
        }
        validationWarnings={
          studyChecksEnabled || !state.projectId || isLoading
            ? undefined
            : launchBlockers.map((check) => check.message)
        }
        onFixCheckWithAi={
          isLoading || !canUseAi ? undefined : handleFixCheckWithAi
        }
        onNavigateToCheck={handleNavigateToCheck}
        onDismissCheck={handleDismissCheck}
        onOpenStudyChecks={
          state.projectId ? studyChecksQuery.recheck : undefined
        }
        onRecheckStudyChecks={
          state.projectId && studyChecksQuery.isError
            ? studyChecksQuery.recheck
            : undefined
        }
        canRecheckStudyChecks={canRecheckStudyChecks}
        onReviewStudyWithAi={handleReviewStudyWithAi}
        canReviewStudyWithAi={canRecheckStudyChecks && !isLoading}
        recheckStudyChecksDisabledMessage={unsavableScreenerMessage}
        reviewStudyWithAiDisabledMessage={
          isLoading
            ? t`Wait for the current AI response to finish.`
            : unsavableScreenerMessage
        }
        studyCheckStatus={studyChecksQuery.status}
        ctaLabel={t`Review`}
        onCtaClick={handleLaunch}
        ctaDisabled={
          !state.projectId ||
          !isHydrated ||
          isLoading ||
          screenerConflict ||
          state.saveStatus === 'saving' ||
          studyChecks.some((check) => check.severity === 'blocker')
        }
      >
        <SmallScreenNotice />

        <Group
          orientation="horizontal"
          // Glide the panels' width (inline flex-grow) on collapse/expand.
          // Compositor-scheduled CSS, not per-frame React updates — it starts
          // moving in ~26ms and stays smooth (a state-gated JS tween here stalled
          // the main thread ~200ms then snapped). The `:has(> ...active)` guard
          // disables the transition during a live drag so the panel tracks the
          // pointer 1:1 instead of lagging behind it. Keep the child combinator:
          // a descendant `:has()` here (especially inside `:not()`) makes any
          // DOM change in the editor, e.g. each keystroke in a textarea,
          // restyle the whole subtree (~60ms per key).
          className="h-full px-0 py-4 [&_[data-panel]]:transition-[flex-grow] [&_[data-panel]]:duration-200 [&_[data-panel]]:ease-[cubic-bezier(0.32,0.72,0,1)] [&:has(>[data-separator=active])_[data-panel]]:transition-none"
        >
          {/* Chat panel — resizable + collapsible */}
          <Panel
            id="chat"
            panelRef={chatPanelRef}
            collapsible
            collapsedSize={0}
            defaultSize={initialChatCollapsed ? '0%' : '30%'}
            minSize="20%"
            maxSize="40%"
            onResize={(size) =>
              startTransition(() => setIsChatCollapsed(size.asPercentage === 0))
            }
            className="ml-4 h-full min-w-70"
          >
            <ChatPane
              messages={messages}
              status={status}
              currentStep={state.currentStep}
              isLoading={isLoading}
              restoredMessages={restoredMessages}
              lastSessionTime={lastSessionTime}
              stepOrder={stepOrder}
              completion={stepCompletion.status}
              selectedStudyTypeId={selectedStudyTypeId}
              onProjectTypeSelect={handleProjectTypeSelect}
              isAiModEnabled={canSelectAiModeration}
              onSend={handleSend}
              onStop={handleStop}
              stoppedMessageIds={stoppedMessageIds}
              onStepNavigate={handleStepNavigate}
              onLaunch={handleLaunch}
            />
          </Panel>

          <ResizeHandle />

          {/* Editor */}
          <Panel id="editor" minSize="30%">
            <ProjectCanvasContent
              state={state}
              project={project ?? undefined}
              onOutlineChange={handleOutlineChange}
              onScreenerChange={handleScreenerChange}
              onWelcomeScreenChange={handleWelcomeScreenChange}
              onClosingMessageChange={handleClosingMessageChange}
              onInterviewSectionsChange={handleInterviewSectionsChange}
              onBeforeGenerateMediaContext={handleBeforeGenerateMediaContext}
              canGenerateNotes={canUseAi}
              onTargetingClick={handleTargetingClick}
              onProjectTypeClick={handleProjectTypeClick}
              screenerAlert={
                screenerConflict ? (
                  <ScreenerConflictAlert
                    description={
                      <Trans>
                        Someone else changed the screener questions since you
                        opened this project, so saving is paused. Get the latest
                        version (your edits stay one undo away), or save yours
                        anyway.
                      </Trans>
                    }
                    onGetLatest={handleConflictGetLatest}
                    onOverwrite={handleConflictOverwrite}
                  />
                ) : null
              }
              isChatOpen={canUseAi && !isChatCollapsed}
              onToggleChat={canUseAi ? handleToggleChatClick : undefined}
              onTogglePreview={handleTogglePreview}
              onPreviewQuestion={handlePreviewQuestion}
              onPreviewInterviewQuestion={handlePreviewInterviewQuestion}
              onPreviewWelcome={handlePreviewWelcome}
              onPreviewClosing={handlePreviewClosing}
              isStreaming={status === 'streaming' || isLoading}
              showAiModSections={
                state.kindOfResearch === KindOfResearch.AI_MODERATED_INTERVIEW
              }
              stepOrder={stepOrder}
              stepCompletion={stepCompletion.status}
              onAiEditAction={canUseAi ? handleAiEditAction : undefined}
              onOutlineFlagFix={canUseAi ? handleOutlineFlagFix : undefined}
              onQuestionFlagFix={canUseAi ? handleQuestionFlagFix : undefined}
              isFlagFixing={isFlagFixing}
              spotlightRequest={spotlightRequest}
              onUndo={handleUndo}
              onRedo={handleRedo}
              canUndo={state._history.past.length > 0}
              canRedo={state._history.future.length > 0}
              maxQuestions={screenerMaxQuestions}
              minQuestions={screenerMinQuestions}
              isFileUploadQuestionEnabled={isFileUploadQuestionEnabled}
              showNewQuestionTypeBadges={isAdvancedScreenerAnnouncementEnabled}
              rankQuestionEnabled={rankQuestionEnabled}
              matrixQuestionEnabled={matrixQuestionEnabled}
              usabilityTestingEnabled={usabilityTestingEnabled}
              previousQuestions={previousQuestionsConfig}
              onQuestionFocus={handleQuestionFocus}
              quotaSection={quotaSection}
              participantSourcesSection={participantSourcesSection}
              showTargeting={!showParticipantSources}
              showScreener={showScreener}
            />
          </Panel>

          <ResizeHandle />

          {/* Preview panel — resizable + collapsible */}
          <Panel
            id="preview"
            panelRef={previewPanelRef}
            collapsible
            collapsedSize={0}
            defaultSize={previewDefaultOpen ? '30%' : 0}
            maxSize="50%"
            minSize="420px"
            className="mr-4 h-full min-w-105 overflow-clip rounded-xl border border-secondary bg-primary shadow-xs"
            onResize={(size) =>
              startTransition(() =>
                setIsPreviewCollapsed(size.asPercentage === 0),
              )
            }
          >
            <ProjectPreview
              name={state.outline.name}
              description={state.outline.description}
              questions={state.screenerQuestions}
              kindOfResearch={state.kindOfResearch}
              reward={
                project?.incentive != null
                  ? formatCurrency(project.incentive)
                  : undefined
              }
              // AI-mod uses the live estimate so an empty script shows the
              // placeholder rather than the default duration.
              durationMinutes={
                isAiModStudy(state.kindOfResearch)
                  ? (inferredAiDuration ?? undefined)
                  : (state.duration ?? undefined)
              }
              welcomeScreen={state.welcomeScreen}
              closingMessage={state.closingMessage}
              interviewSections={state.interviewSections}
              interviewDeliveryMode={state.interviewDeliveryMode}
              studyLanguage={state.studyLanguage}
              projectId={isNewProject ? null : projectId}
              isOpen={!isPreviewCollapsed}
              onClose={handleClosePreview}
              previewJump={previewJump}
              recordingPanel={previewRecordingPanel}
            />
          </Panel>
        </Group>

        {(project ?? draftProject) && (
          <TargetAudienceDrawer
            project={project ?? draftProject}
            projectId={projectId}
            isOpen={isTargetingDrawerOpen}
            onProjectCreated={handleProjectCreated}
            onClose={(newProjectId?: string) => {
              setIsTargetingDrawerOpen(false);
              // Draft close-without-save has no persisted project to sync from.
              // After save, use the newly created project id returned by
              // useDraftSave. The indirection (save happens on the drawer, and
              // we re-read the data from query cache) is not ideal. there's
              // a ticket to refactor: https://linear.app/respondent/issue/RIO-583/refactor-project-creation-form
              if (isNewProject && !newProjectId) return;

              const syncProjectId = newProjectId ?? projectId;

              // Re-sync AI state from the (now-updated) query cache
              const updated =
                queryClient.getQueryData<GetResearcherProjectResponse>(
                  researcherProjectQueryOptions(syncProjectId).queryKey,
                );
              if (updated?.success) {
                const p = updated.data;
                const newTargeting = buildTargetingFromProject(p);

                // Dispatch targeting changes as a tracked batch so undo works
                const actions: AiProjectAction[] = (
                  Object.keys(newTargeting) as Array<keyof typeof newTargeting>
                ).map((field) => ({
                  type: 'PATCH_TARGETING' as const,
                  field,
                  value: newTargeting[field],
                }));
                dispatch({
                  type: 'HISTORY_BATCH',
                  label: 'Update targeting',
                  actions,
                  spotlight: { section: 'targeting' },
                });

                // Re-sync non-targeting fields (non-tracked)
                dispatch({
                  type: 'HYDRATE_FROM_PROJECT',
                  outline: {
                    name: p.name || '',
                    internalName: p.internalName || '',
                    description: p.description || '',
                    studyGoals: p.studyGoals || '',
                  },
                  targeting: buildTargetingFromProject(p),
                  screenerQuestions: p.screenerQuestions
                    ? apiQuestionsToForm(p.screenerQuestions)
                    : [],
                });
                // The reducer's screener was just rebuilt from `p`, so `p` is
                // the new basis for optimistic concurrency (ONCALL-2139).
                screenerBasisVersionRef.current = p.screenerVersion ?? 0;
              }
            }}
            googleApiKey={googleApiKey}
          />
        )}
        <StudyTypeDrawer
          project={draftProject}
          projectId={projectId}
          isOpen={isStudyTypeDrawerOpen}
          hideAiInterview={!canSelectAiModeration}
          aiInferredDurationMinutes={inferredAiDuration}
          googleApiKey={googleApiKey}
          onSave={handleStudyTypeSave}
          onClose={() => setIsStudyTypeDrawerOpen(false)}
        />
        <AdvancedScreenerAnnouncement
          isOpen={advancedScreenerAnnouncement.isOpen}
          onDismiss={advancedScreenerAnnouncement.dismiss}
        />
      </ProjectFlowLayout>
    </QuestionChecksContext.Provider>
  );
}
